import Vue from 'vue';
import {CHAT_FUNCTION_HANDLERS} from "@frontend/store/creative-studio/chat-function-handlers";
import creativeStudioAiFunctions from "@frontend/store/creative-studio/creative-studio-ai-functions";
import { generateAndShowBackgroundPreviews, searchAndShowProductImages, resolveAttachmentReferences } from "@frontend/store/creative-studio/chat-function-handlers/search-products";
import { tryResumeReferenceQueue, tryResumeGs1DescribeQueue, tryResumeGs1ReferenceQueue, tryResumeGs1DescribeForPrep, tryResumeGs1ReferenceForPrep, startAbHeroGrid, retryAbHeroTile } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { createCreativeStudioPdpContextAdapter } from '@frontend/store/creative-studio/chat-function-handlers/cs-pdp-adapter';
import { extractAsin, extractMarketplaceDomain } from '@frontend/store/creative-studio/amazon-asin';
import { continueWithReferenceImages, fetchReferenceImagesAndContinue, parseTypedReference } from '@frontend/store/creative-studio/chat-function-handlers/create-pdp-image-set';
import { revealSequencePickerWithProduct } from '@frontend/store/creative-studio/chat-function-handlers/sequence-handoff';
import { resolveProductOverlayUrl } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { activeConversationKey, NEW_CHAT_POINTER } from '@frontend/store/creative-studio/active-conversation-key';

const CHAT_LOCALSTORAGE_KEY = 'ai-chat:creative-studio';
const PENDING_DESIGN_JOBS_KEY = 'ai-chat:creative-studio:pending-design-jobs';
const RESOLVED_DESIGN_JOBS_KEY = 'ai-chat:creative-studio:resolved-design-jobs';
const PARSED_PROMPT_KEY = 'ai-chat:creative-studio:parsed-prompt';

// POR-13504: message-writing dispatches/commits that must NOT run once the user
// has left the conversation a generation was started in (clicked "New" / opened
// another chat). A stale generation's late async result would otherwise leak
// into — and be persisted onto — the now-active chat.
const CONVERSATION_GUARDED_ACTIONS = new Set([
  'addAndSaveMessage',
  'addAndSaveMessages',
  'saveMessages',
  'updateAndSaveMessage',
  // Also drop conversation-state persistence so a stale handler can't overwrite
  // the now-active chat's parsed prompt / selection with the old chat's.
  'saveConversationState',
]);
const CONVERSATION_GUARDED_MUTATIONS = new Set(['ADD_MESSAGE', 'UPDATE_MESSAGE']);

// Wrap a namespaced Vuex context so message-writing dispatches/commits become
// no-ops once the active conversation no longer matches `ownerConversationId`
// (the conversation the generation belongs to) — i.e. the user hit "New" or
// opened another chat mid-generation. Non-write actions/mutations pass through
// untouched, so cleanup (setProcessing(false), input clearing, …) still runs.
// A null owner (a surface with no conversation concept) disables the guard, so
// non-CS surfaces behave exactly as before. (POR-13504)
function withConversationGuard({ state, commit, dispatch, getters }, ownerConversationId) {
  const isStale = () => ownerConversationId != null
    && ownerConversationId !== (state.activeConversation ? state.activeConversation.id : null);
  return {
    state,
    getters,
    isStale,
    commit: (type, payload, options) => {
      if (CONVERSATION_GUARDED_MUTATIONS.has(type) && isStale()) return undefined;
      return commit(type, payload, options);
    },
    dispatch: (type, payload, options) => {
      if (CONVERSATION_GUARDED_ACTIONS.has(type) && isStale()) return Promise.resolve(undefined);
      return dispatch(type, payload, options);
    },
  };
}

export function normalizeCategoryKey(category) {
  const value = String(category || '').toLowerCase();
  if (value.includes('retail')) return 'retail';
  if (value.includes('social')) return 'social';
  if (value.includes('pdp')) return 'pdp';
  if (value.includes('print') || value.includes('marketing')) return 'print_marketing';
  return 'other';
}

export function normalizeNetworks(networks) {
  return networks.map((network) => ({
    public_id: network.public_id || network.id,
    db_id: typeof network.db_id !== 'undefined' ? Number(network.db_id) : null,
    name: network.name || '',
    category: network.category || '',
    categoryKey: normalizeCategoryKey(network.category),
    placementTypes: (network.placementTypes || []).map((pt) => ({
      public_id: pt.public_id || pt.id,
      db_id: typeof pt.db_id !== 'undefined' ? Number(pt.db_id) : null,
      label: pt.label || '',
      sort_order: Number(pt.sort_order ?? 0),
      sizes: (pt.sizes || []).map((size) => ({
        public_id: size.public_id || size.id,
        db_id:
          typeof size.db_id !== 'undefined'
            ? Number(size.db_id)
            : typeof size.id === 'number'
              ? size.id
              : null,
        name: size.name || size.label || '',
        width: Number(size.width ?? size.w ?? 0),
        height: Number(size.height ?? size.h ?? 0),
      })),
    })),
    onsite: network.onsite,
    offsite: network.offsite,
  }));
}

export function getCreatePlacementTypes(network) {
  if (network.placementTypes && network.placementTypes.length) {
    return network.placementTypes.map((pt) => ({
      label: pt.label,
      sizes: (pt.sizes || []).map((s) => {
        const width = Number(s.width ?? s.w ?? 0);
        const height = Number(s.height ?? s.h ?? 0);
        return {
          id: s.db_id ?? s.public_id,
          label: s.name || s.label || `${width}x${height}`,
          width,
          height,
        };
      }),
    }));
  }

  return [
    {
      label: 'Onsite',
      sizes: (network.onsite?.sizes || []).map((s) => {
        const width = Number(s.width ?? s.w ?? 0);
        const height = Number(s.height ?? s.h ?? 0);
        return {
          id: s.db_id ?? s.id ?? s.public_id,
          label: s.name || s.label || `${width}x${height}`,
          width,
          height,
        };
      }),
    },
    {
      label: 'Offsite',
      sizes: (network.offsite?.sizes || []).map((s) => {
        const width = Number(s.width ?? s.w ?? 0);
        const height = Number(s.height ?? s.h ?? 0);
        return {
          id: s.db_id ?? s.id ?? s.public_id,
          label: s.name || s.label || `${width}x${height}`,
          width,
          height,
        };
      }),
    },
  ].filter((pt) => pt.sizes.length > 0);
}

// Attachment props that come from the server and should never be carried
// over from local state (they refer to the persisted entity itself).
const SERVER_OWNED_ATTACHMENT_KEYS = new Set([
  'id', 'message_id', 'path', 'url', 'mime_type', 'size',
  'created_at', 'updated_at',
  'type', 'payload', 'preview', 'fileType', 'name',
]);

// When the server returns the persisted version of a message, the attachment
// objects only carry the server-owned fields above. Client-only display props
// like `overlayUrl` (Cambridge / GS1 packshot composite) and `_mrhiCallouts`
// (Cambridge text placeholders) are NOT serialized — saveMessages sends only
// `attachments_urls[]`. Without this merge, REPLACE_MESSAGE_BY_* would wipe
// those props as soon as the POST /messages roundtrip resolves, and the chat
// would re-render the bare Gemini background (1s flash → blank composite).
// Match by index since the server preserves the order it received attachments
// in. Only client-only keys are copied — server fields always win.
const mergeAttachmentClientExtras = (serverAtts = [], localAtts = []) => {
  if (!Array.isArray(serverAtts) || !serverAtts.length) {
    return serverAtts || [];
  }
  if (!Array.isArray(localAtts) || !localAtts.length) {
    return serverAtts;
  }
  return serverAtts.map((srv, i) => {
    const local = localAtts[i];
    if (!local || typeof local !== 'object') return srv;
    const extras = {};
    Object.keys(local).forEach((key) => {
      if (SERVER_OWNED_ATTACHMENT_KEYS.has(key)) return;
      if (local[key] === undefined || local[key] === null) return;
      extras[key] = local[key];
    });
    return Object.keys(extras).length ? { ...srv, ...extras } : srv;
  });
};

const normalizePersistedMessage = (message) => {
  const withMeta = applyMessageMeta(message);

  // Compositional PDP types (Hero / Cambridge / GS1 separate-mode) keep their
  // overlay packshot URL in `_pdpImageContext.overlayUrl` (persisted via meta)
  // but the chat renderer reads `att.overlayUrl` and `att._mrhiCallouts`
  // directly off the attachment object. The server doesn't store either of
  // those — only the bare image URL. Without rehydrating here, a fresh load
  // (page reload, second cross-page recovery) drops the composite and the
  // chat shows just the blank Gemini background. Apply only to the first
  // attachment, which is where the queue stores them. Mirror the conditions
  // in pdp-generation-paths.js so we don't fabricate an overlay for integrated
  // results (whose Gemini output already includes the product) or standalone
  // text-only types (Nutrition Facts), where adding an overlay would draw a
  // packshot on top of an image that doesn't expect one.
  const PDP_STANDALONE_LAYER_TYPES = ['nutrition_facts'];
  const ctx = withMeta._pdpImageContext;
  const ctxIsSeparateMode = ctx && ctx.productMode === 'separate' && !PDP_STANDALONE_LAYER_TYPES.includes(ctx.type);
  const ctxOverlayUrl = ctxIsSeparateMode && typeof ctx.overlayUrl === 'string' ? ctx.overlayUrl : null;
  const ctxNeedsCambridgeCallouts = ctxIsSeparateMode && ctx.type === 'mobile_hero_cambridge';
  // Gallery-mode PDP rows store the template thumbnail in
  // `_pdpImageContext.templateThumb` so it survives the saveMessages roundtrip
  // (the server's persisted attachments_urls[] drops the relative share URL).
  // When the server returns no attachments for a gallery row, synthesize one
  // from the context so the chat keeps showing the picked template thumbnail.
  const ctxGalleryThumb = ctx && ctx.rowMode === 'gallery' && typeof ctx.templateThumb === 'string' && ctx.templateThumb
    ? ctx.templateThumb
    : null;

  let firstAttachmentSeen = false;

  const normalizedAttachments = (withMeta.attachments || []).map((att) => {
      const fileType = String(att.mime_type || att.fileType || '');
      const name = String(att.name || att.path || '').toLowerCase();
      const url = att.url || att.payload || '';

      const isImage =
        fileType.startsWith('image/') ||
        /\.(png|jpe?g|webp|gif)$/i.test(name) ||
        String(url).includes('/share?file=');

      const isPsd = name.endsWith('.psd');

      const normalized = {
        ...att,
        type: isImage ? 'url' : 'file',
        payload: url,
        preview: isImage && !isPsd ? url : '',
        fileType: fileType,
        name: att.name || (att.path ? att.path.split('/').pop() : ''),
      };

      if (!firstAttachmentSeen && isImage) {
        firstAttachmentSeen = true;
        if (ctxOverlayUrl && !normalized.overlayUrl) {
          normalized.overlayUrl = ctxOverlayUrl;
        }
        if (ctxNeedsCambridgeCallouts && !normalized._mrhiCallouts) {
          normalized._mrhiCallouts = {
            type: 'cambridge',
            flavorText: 'PRODUCT OR FLAVOR',
            countText: 'Count',
          };
        }
      }

      return normalized;
    });

  const attachments = (normalizedAttachments.length === 0 && ctxGalleryThumb)
    ? [{
      type: 'url',
      payload: ctxGalleryThumb,
      preview: ctxGalleryThumb,
      name: `${(ctx && ctx.label) || 'template'}.png`,
      fileType: 'image/png',
    }]
    : normalizedAttachments;

  return {
    ...withMeta,

    widgets: Array.isArray(withMeta.widgets) ? withMeta.widgets : [],

    _widgetValues:
      withMeta._widgetValues && !Array.isArray(withMeta._widgetValues)
        ? withMeta._widgetValues
        : {},

    disabled: !!withMeta.disabled,

    attachments,
  };
};

const payloadPathname = (payload) => {
  try {
    const url = new URL(payload, window.location.origin);
    if (url.pathname === '/share' && url.searchParams.get('file')) {
      return url.searchParams.get('file');
    }
    return url.pathname;
  } catch (_) {
    return payload;
  }
};

const MESSAGE_META_KEYS = [
  '_networkPublicId',
  '_pathType',

  '_foundProducts',
  '_missingProductNames',

  '_pdpImageContext',
  '_pdpImageKept',
  '_pdpImageSkipped',
  '_pdpGeneratedImages',
  '_pdpStylePickerContext',
  '_pdpQueueFailure',
  '_pdpParallelGrid',
  '_abHeroForm',
  '_abHeroResult',

  '_backgroundOptions',
  '_backgroundScene',
  '_selectedBackgroundUrl',

  '_placeProductContext',

  '_progressDurationMs',
  '_progressStartedAt',
  '_batchLayeredPdpJob',
  '_sequenceConfirmedAt',
  '_pdpSetupContext',
  '_pendingPrompt',
];

const extractMessageMeta = (message = {}) => {
  const meta = {};

  MESSAGE_META_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(message, key)) {
      meta[key] = message[key];
    }
  });

  return meta;
};

/**
 * On (re)load, transient in-flight UI state that was persisted but whose driving
 * JS promise is gone after a reload is normalized so the chat never shows a frozen
 * "in progress" affordance:
 *  - parallel-grid tiles left `generating`/`regenerating` → `error` (retryable). (POR-13183)
 *  - A/B hero result tiles left `generating`/`regenerating` → `done` if the tile still
 *    has its previous image (a re-roll was in flight over a completed one), otherwise
 *    `error` so the Retry button appears. Without this an interrupted A/B generation
 *    (tab closed / navigation / dropped job) reopened from history shows a card stuck
 *    on the "Regenerating…" overlay or blank spinner forever, unrecoverable. (POR-13563)
 *  - the A/B hero form's `generating` flag → cleared (it renders "Generating ···" and
 *    disables the whole form otherwise). If the generation never produced a result
 *    message (interrupted before it persisted), the form is also re-enabled so the user
 *    can re-generate instead of being stuck on a disabled, spinning form. (POR-13563)
 *  - the animated progress bar (`_progress*`) on long-running flows (A/B "Create
 *    layout", PDP generation) → cleared. Otherwise, after the user opens the
 *    created layout and comes back to the chat, the bar keeps animating because
 *    `shouldShowProgress` recomputes from the persisted `_progressStartedAt`. The
 *    operation that animated it isn't running in this freshly loaded page. (POR-13385-2)
 */
function normalizeStaleMessages(messages) {
  const list = messages || [];
  list.forEach((m, idx) => {
    const grid = m && m._pdpParallelGrid;
    if (grid && Array.isArray(grid.tiles)) {
      const hasStale = grid.tiles.some((t) => t.state === 'generating' || t.state === 'regenerating');
      if (hasStale) {
        m._pdpParallelGrid = {
          ...grid,
          tiles: grid.tiles.map((t) => (
            (t.state === 'generating' || t.state === 'regenerating') ? { ...t, state: 'error' } : t
          )),
        };
      }
    }

    const abGrid = m && m._abHeroResult;
    if (abGrid && Array.isArray(abGrid.tiles)) {
      const hasStale = abGrid.tiles.some((t) => t.state === 'generating' || t.state === 'regenerating');
      if (hasStale) {
        m._abHeroResult = {
          ...abGrid,
          tiles: abGrid.tiles.map((t) => (
            (t.state === 'generating' || t.state === 'regenerating')
              ? { ...t, state: t.url ? 'done' : 'error', slow: false, saving: false }
              : t
          )),
        };
      }
    }

    const abForm = m && m._abHeroForm;
    if (abForm && abForm.generating) {
      m._abHeroForm = { ...abForm, generating: false };
      // Re-enable only when the generation was interrupted before producing any result.
      // A completed round carries its result either on the SAME message (unified
      // `ab_hero` widget — form + result co-located, POR-13765) or on a following message
      // (legacy two-message flow); both stay intentionally locked. Only a form with no
      // result anywhere was interrupted. (POR-13563)
      const resultFollows = !!(m && m._abHeroResult)
        || list.slice(idx + 1).some((x) => x && x._abHeroResult);
      if (!resultFollows) {
        m.disabled = false;
      }
    }

    const batchJobActive = ['pending', 'processing'].includes(m?._batchLayeredPdpJob?.status);
    if (m && !batchJobActive && (m._progressStartedAt || m._progressDurationMs)) {
      m._progressStartedAt = null;
      m._progressDurationMs = null;
    }
  });
  return messages;
}

const applyMessageMeta = (message = {}) => {
  const meta = message.meta && typeof message.meta === 'object'
    ? message.meta
    : {};

  return {
    ...message,
    ...meta,
  };
};

export default {
  namespaced: true,

  state: () => ({
    activeTab: 'create',
    // How to compose the banner: 'auto' (let the model decide via background_scene),
    // 'layered' (real product cutout + editable text layers) or 'flat' (one generated
    // image). User-controlled via the composer toggle. (POR-13481)
    layoutMode: 'auto',
    chatText: '',
    showSizesModal: false,
    saving: false,
    selectedSizeIds: new Set(),
    selectedReferences: [],
    selectedTentpole: null,
    imagePrompt: "",
    allNetworks: [],
    selectedPlacement: null,
    createOpenRows: {},
    createOpenSections: {},
    configureOpenSections: {},
    configureOpenRows: {},
    openPlacements: {},
    isProcessingChatMessage: false,
    isClearingChat: false,
    // One-shot flag: when true, the next sendMessage skips the "you referenced an
    // attached image but none is attached" guard (used by "Continue without"). (POR-13481)
    bypassAttachmentCheck: false,
    // The prompt to auto-resume once the user provides the image the missing-attachment
    // guard asked for — set when the guard fires, consumed when they attach/paste/pick
    // an image (so it's used for the banner instead of a bare "Nice product shot"). (POR-13481)
    pendingAttachPrompt: null,
    messages: [],
    attachments: [],
    showcaseTemplates: [],
    parsedPrompt: null,
    selectedBackgroundUrl: null,
    pendingFolderSection: null,
    pendingBackgroundDescription: null,
    pendingBackgroundSize: null,
    pendingPsdImport: false,
    pendingPdpRegeneration: null,
    // Set by the PDP fast-path when the user clicks the "Create a PDP image
    // [set]" hero card with no product context. The next user reply is the
    // product info — sendMessage resumes the PDP flow synthetically without
    // a /call-open-ai roundtrip. Shape: { isSet: bool } | null.
    pendingPdpIntent: null,
    // GS1 "describe the changes" step (POR-13271): drives the dock input
    // highlight + the clickable sample link. Shape: { rowIndex, label, example } | null.
    pendingPdpRefine: null,
    // POR-13321: drives the "Got <product>. Match a style or generate fresh?"
    // reference step. When set, the dock input is highlighted/focused and the
    // next user message is routed as a reference ASIN/GTIN (NOT through the LLM,
    // which reliably reverses target/reference — see POR-12923).
    // Shape: { productReferences, label, example } | null.
    pendingPdpReference: null,
    // POR-13612-4: the "match a style or generate fresh?" ask is showing and the
    // user can still type instead of clicking a button. When set, a typed ASIN or
    // Amazon URL is guessed as the reference (as if "ASIN" was clicked, then
    // pasted); non-ASIN text clears this and flows on normally.
    // Shape: { productReferences } | null.
    pendingPdpReferenceAsk: null,
    // POR-13612-5-2: a cross-sell needs a SECOND product (Product 2). When a set
    // with a cross-sell is confirmed, we ask for it and stash the confirm payload
    // here; the answer (typed ASIN/URL, upload, or library pick) resumes the set.
    // Shape: { payloadJson, messageIndex } | null.
    pendingCrossSellProduct: null,
    pendingTranslateImageIntent: null,
    pendingTranslateImageWaitingForImage: null,
    activeConversation: null,
    loadingMessages: false,
    conversations: [],
    loadingConversations: false,
    sendingMessage: false,
    // In-flight async generations awaiting a Pusher event. Each entry:
    // { uuid, resultMessage, replacementPlaceholder } captures the message that
    // should be added once the URL resolves so that, after a page reload, the
    // chat can rebuild the same composite (overlay + callouts + ctx) it would
    // have rendered in-session.
    pendingDesignJobs: [],
    // UUIDs that have already had their snapshot applied to the chat (either
    // mid-session via Pusher or on mount via the recovery sweep). Stored so a
    // subsequent /creative-studio?cs_uuid=... visit does not add a duplicate
    // message. Capped at 50 entries (FIFO) inside the mutation to keep the
    // list small.
    resolvedDesignJobs: [],
    pendingPlacementDescribe: false,
    isDescribingPlacement: false,
    pendingProofAfterLayout: false,
    autoTriggerCreateLayout: false,
    sessionLayoutLink: null,
    sessionProofLink: null,
  }),

  getters: {
    createSections(state) {
      return [
        { key: 'retail', label: 'Retail Media Networks', networks: state.allNetworks.filter((n) => n.categoryKey === 'retail') },
        { key: 'social', label: 'Social Platforms',      networks: state.allNetworks.filter((n) => n.categoryKey === 'social') },
        { key: 'pdp',    label: 'PDP Images',            networks: state.allNetworks.filter((n) => n.categoryKey === 'pdp') },
        { key: 'print_marketing', label: 'Print & Marketing', networks: state.allNetworks.filter((n) => n.categoryKey === 'print_marketing') },
        { key: 'other',  label: 'Other',                 networks: state.allNetworks.filter((n) => n.categoryKey === 'other')  },
      ].filter((s) => s.networks.length > 0);
    },
    configureSections(state) {
      return [
        { key: 'retail', label: 'Retail Media Networks', networks: state.allNetworks.filter((n) => n.categoryKey === 'retail') },
        { key: 'social', label: 'Social Platforms',      networks: state.allNetworks.filter((n) => n.categoryKey === 'social') },
        { key: 'pdp',    label: 'PDP Images',            networks: state.allNetworks.filter((n) => n.categoryKey === 'pdp') },
        { key: 'print_marketing', label: 'Print & Marketing', networks: state.allNetworks.filter((n) => n.categoryKey === 'print_marketing') },
        { key: 'other',  label: 'Other',                 networks: state.allNetworks.filter((n) => n.categoryKey === 'other')  },
      ].filter((s) => s.networks.length > 0);
    },
    createSelectionText(state) {
      const total = state.selectedSizeIds.size;
      return total === 0 ? '0 sizes selected' : `${total} sizes selected`;
    },
    selectedChips(state, getters) {
      const chips = [];
      getters.createSections.forEach((section) => {
        section.networks.forEach((network) => {
          getCreatePlacementTypes(network).forEach((pt) => {
            pt.sizes.forEach((size) => {
              if (state.selectedSizeIds.has(size.id)) {
                const dimensions = `${size.width}×${size.height}`;
                const name = size.label && size.label !== dimensions ? size.label : '';
                chips.push({
                  id: size.id,
                  name,
                  dimensions,
                  network: network.name || '',
                  placement: pt.label || '',
                });
              }
            });
          });
        });
      });
      return chips;
    },
    lastGeneratedImage(state) {
      if (!state.messages || !state.messages.length) return null;
      
      // Find latest assistant message with attachments
      const assistantMsgs = [...state.messages].reverse().filter(m => m.role === 'assistant');
      for (const msg of assistantMsgs) {
        if (msg.attachments && msg.attachments.length) {
          const img = msg.attachments.find(a => a.type === 'url');
          if (img) return img.payload;
        }
      }
      return null;
    },
    selectedReferenceKeys(state) {
      return Array.isArray(state.selectedReferences) ? state.selectedReferences : [];
    },
    selectedTentpole(state) {
      return state.selectedTentpole;
    },
  },

  mutations: {
    SET_ACTIVE_TAB(state, tab) {
      state.activeTab = tab;
    },
    PUSH_PENDING_DESIGN_JOB(state, entry) {
      state.pendingDesignJobs = [...(state.pendingDesignJobs || []), entry];
    },
    REMOVE_PENDING_DESIGN_JOB(state, uuid) {
      state.pendingDesignJobs = (state.pendingDesignJobs || []).filter((e) => e.uuid !== uuid);
    },
    SET_PENDING_DESIGN_JOBS(state, list) {
      state.pendingDesignJobs = Array.isArray(list) ? list : [];
    },
    MARK_DESIGN_JOB_RESOLVED(state, uuid) {
      const list = Array.isArray(state.resolvedDesignJobs) ? state.resolvedDesignJobs : [];
      if (list.includes(uuid)) return;
      const next = [...list, uuid];
      // Cap at 50 entries (FIFO) — anything older is unlikely to be re-clicked
      // from a notification because the bell only lists ~100 recent entries.
      state.resolvedDesignJobs = next.length > 50 ? next.slice(-50) : next;
    },
    SET_RESOLVED_DESIGN_JOBS(state, list) {
      state.resolvedDesignJobs = Array.isArray(list) ? list : [];
    },
    SET_CHAT_TEXT(state, text) {
      state.chatText = text;
    },
    SET_LAYOUT_MODE(state, mode) {
      state.layoutMode = ['auto', 'layered', 'flat'].includes(mode) ? mode : 'auto';
    },
    SET_BYPASS_ATTACHMENT_CHECK(state, val) {
      state.bypassAttachmentCheck = !!val;
    },
    SET_PENDING_ATTACH_PROMPT(state, prompt) {
      state.pendingAttachPrompt = prompt || null;
    },
    SET_SHOW_SIZES_MODAL(state, val) {
      state.showSizesModal = val;
    },
    SET_SAVING(state, val) {
      state.saving = val;
    },
    SET_IMAGE_PROMPT(state, prompt) {
      state.imagePrompt = prompt;
    },
    SET_PROCESSING_CHAT_MESSAGE(state, val) {
      state.isProcessingChatMessage = val;
    },
    SET_ALL_NETWORKS(state, networks) {
      state.allNetworks = networks;
    },
    SET_SELECTED_PLACEMENT(state, placement) {
      state.selectedPlacement = placement;
    },
    SET_SELECTED_REFERENCES(state, refs) {
      state.selectedReferences = Array.isArray(refs) ? refs : [];
    },
    TOGGLE_REFERENCE(state, refKey) {
      const key = String(refKey || '').trim();
      if (!key) return;
      const next = new Set(Array.isArray(state.selectedReferences) ? state.selectedReferences : []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      state.selectedReferences = Array.from(next);
    },
    REMOVE_REFERENCE(state, refKey) {
      const key = String(refKey || '').trim();
      if (!key) return;
      state.selectedReferences = (Array.isArray(state.selectedReferences) ? state.selectedReferences : []).filter((k) => k !== key);
    },
    REMOVE_REFERENCES_FOR_RESOURCE(state, { kind, id }) {
      const kindStr = String(kind || '');
      const idStr = String(id ?? '');
      if (!kindStr || idStr === '') return;
      state.selectedReferences = (Array.isArray(state.selectedReferences) ? state.selectedReferences : [])
        .filter((key) => {
          const [refKind = '', refId = ''] = String(key).split('|');
          return refKind !== kindStr || refId !== idStr;
        });
    },
    CLEAR_REFERENCES(state) {
      state.selectedReferences = [];
    },
    SET_TENTPOLE_SEASONAL(state, payload) {
      state.selectedTentpole = payload && payload.id
        ? { id: String(payload.id), label: String(payload.label || payload.id) }
        : null;
    },
    CLEAR_TENTPOLE_SEASONAL(state) {
      state.selectedTentpole = null;
    },
    TOGGLE_SIZE(state, { sizeId, selected }) {
      const next = new Set(state.selectedSizeIds);
      if (selected) next.add(sizeId);
      else next.delete(sizeId);
      state.selectedSizeIds = next;
    },
    CLEAR_SELECTION(state) {
      state.selectedSizeIds = new Set();
    },
    SELECT_ALL(state, ids) {
      state.selectedSizeIds = new Set(ids);
    },
    SET_CREATE_ROW_OPEN(state, { id, val }) {
      state.createOpenRows = { ...state.createOpenRows, [id]: val };
    },
    SET_CONFIGURE_SECTION_OPEN(state, { key, val }) {
      state.configureOpenSections = { ...state.configureOpenSections, [key]: val };
    },
    SET_CONFIGURE_ROW_OPEN(state, { id, val }) {
      state.configureOpenRows = { ...state.configureOpenRows, [id]: val };
    },
    SET_PLACEMENT_OPEN(state, { id, val }) {
      state.openPlacements = { ...state.openPlacements, [id]: val };
    },
    ADD_MESSAGE(state, message) {
      state.messages.push(message);
    },
    UPDATE_MESSAGE(state, { index, payload, patch }) {
      const message = state.messages[index];

      if (!message) {
        console.error('Message not found at index:', index);
        return;
      }

      // Legacy shape: { payload: { attachments } }
      if (payload) {
        message.attachments = payload.attachments ?? [];
      }

      // New shape: { patch: { ...fields } } — merges arbitrary keys reactively
      if (patch) {
        Object.keys(patch).forEach((key) => {
          Vue.set(message, key, patch[key]);
        });
      }
    },
    SET_MESSAGES(state, messages) {
      state.messages = normalizeStaleMessages(messages);
    },
    ADD_ATTACHMENT(state, attachment) {
      state.attachments.push(attachment);
    },
    REMOVE_ATTACHMENT(state, index) {
      if (state.attachments && index >= 0 && index < state.attachments.length) {
        state.attachments.splice(index, 1);
      }
    },
    SET_ATTACHMENTS(state, attachments) {
      state.attachments = attachments;
    },
    SET_IS_CLEARING_CHAT(state, isClearingChat) {
      state.isClearingChat = isClearingChat;
    },
    SET_MESSAGE_DISABLED(state, { index, disabled }) {
      const message = state.messages[index];
      if (message) {
        Vue.set(message, 'disabled', disabled);
      }
    },
    SET_SHOWCASE_TEMPLATES(state, templates) {
      state.showcaseTemplates = templates;
    },
    SET_PARSED_PROMPT(state, parsed) {
      if (parsed === null) {
        state.parsedPrompt = null;
        return;
      }

      state.parsedPrompt = {
        ...(state.parsedPrompt || {}),
        ...(parsed || {}),
      };
    },
    SET_SELECTED_BACKGROUND(state, url) {
      state.selectedBackgroundUrl = url;
    },
    SET_PENDING_FOLDER_SECTION(state, payload) {
      state.pendingFolderSection = payload;
    },
    SET_PENDING_BACKGROUND_DESCRIPTION(state, payload) {
      state.pendingBackgroundDescription = payload;
    },
    SET_PENDING_BACKGROUND_SIZE(state, payload) {
      state.pendingBackgroundSize = payload;
    },
    SET_PENDING_SALSIFY_RETRY(state, payload) {
      state.pendingSalsifyRetry = payload;
    },
    SET_PENDING_PSD_IMPORT(state, val) {
      state.pendingPsdImport = val;
    },
    SET_PENDING_PDP_REGENERATION(state, payload) {
      state.pendingPdpRegeneration = payload;
    },
    SET_PENDING_PDP_INTENT(state, payload) {
      state.pendingPdpIntent = payload;
    },
    SET_PENDING_PDP_REFINE(state, payload) {
      state.pendingPdpRefine = payload;
    },
    SET_PENDING_PDP_REFERENCE(state, payload) {
      state.pendingPdpReference = payload;
    },
    SET_PENDING_PDP_REFERENCE_ASK(state, payload) {
      state.pendingPdpReferenceAsk = payload;
    },
    SET_PENDING_CROSS_SELL_PRODUCT(state, payload) {
      state.pendingCrossSellProduct = payload;
    },
    SET_PENDING_TRANSLATE_IMAGE_INTENT(state, payload) {
      state.pendingTranslateImageIntent = payload;
    },
    SET_PENDING_TRANSLATE_IMAGE_WAITING_FOR_IMAGE(state, payload) {
      state.pendingTranslateImageWaitingForImage = payload;
    },
    SET_ACTIVE_CONVERSATION(state, conversation) {
      state.activeConversation = conversation;
    },
    SET_LOADING_MESSAGES(state, value) {
      state.loadingMessages = value;
    },
    UPDATE_TEMP_MESSAGE(state, { tempId, message }) {
      const index = state.messages.findIndex((m) => m.id === tempId);
      if (index !== -1) {
        state.messages.splice(index, 1, message);
      }
    },
    SET_CONVERSATIONS(state, conversations) {
      state.conversations = conversations;
    },
    ADD_CONVERSATION(state, conversation) {
      state.conversations = [conversation, ...state.conversations];
    },
    SET_LOADING_CONVERSATIONS(state, value) {
      state.loadingConversations = value;
    },
    REMOVE_CONVERSATION(state, conversationId) {
      state.conversations = state.conversations.filter((c) => c.id !== conversationId);

      if (state.activeConversation?.id === conversationId) {
        state.activeConversation = null;
        state.messages = [];
      }
    },
    SET_SENDING_MESSAGE(state, value) {
      state.sendingMessage = value;
    },
    REPLACE_MESSAGE_BY_TEMP_ID(state, { tempId, message }) {
      const index = state.messages.findIndex((m) => m.tempId === tempId);

      if (index !== -1) {
        const current = state.messages[index] || {};
        const normalized = normalizePersistedMessage(message);

        Vue.set(state.messages, index, {
          ...current,
          ...normalized,
          ...extractMessageMeta(current),
          ...extractMessageMeta(normalized),
          ...(current._pdpParallelGrid ? { _pdpParallelGrid: current._pdpParallelGrid } : {}),
          ...(current._abHeroResult ? { _abHeroResult: current._abHeroResult } : {}),
          attachments: mergeAttachmentClientExtras(
            normalized.attachments,
            current.attachments,
          ),
        });
      }
    },
    SET_PENDING_PLACEMENT_DESCRIBE(state, payload) {
      state.pendingPlacementDescribe = payload;
    },
    SET_DESCRIBING_PLACEMENT(state, payload) {
      state.isDescribingPlacement = payload;
    },
    SET_PENDING_PROOF_AFTER_LAYOUT(state, val) {
      state.pendingProofAfterLayout = val;
    },
    SET_AUTO_TRIGGER_CREATE_LAYOUT(state, val) {
      state.autoTriggerCreateLayout = val;
    },
    SET_SESSION_LAYOUT_LINK(state, val) {
      state.sessionLayoutLink = val;
    },
    SET_SESSION_PROOF_LINK(state, val) {
      state.sessionProofLink = val;
    },
    REPLACE_MESSAGE_BY_ID(state, { id, message }) {
      const index = state.messages.findIndex((m) => m.id === id);

      if (index !== -1) {
        const current = state.messages[index] || {};
        const normalized = normalizePersistedMessage(message);

        Vue.set(state.messages, index, {
          ...current,
          ...normalized,
          ...extractMessageMeta(current),
          ...extractMessageMeta(normalized),
          // The parallel grid is client-authoritative during live generation:
          // the server only stores `_pdpParallelGrid`, never modifies it. A save
          // echo that lands after a newer in-memory tile update must NOT revert
          // it (would flip a `generating`/`done` tile back to `queued`). Keep the
          // latest in-memory grid. (POR-13183)
          ...(current._pdpParallelGrid ? { _pdpParallelGrid: current._pdpParallelGrid } : {}),
          ...(current._abHeroResult ? { _abHeroResult: current._abHeroResult } : {}),
          attachments: normalized.attachments?.length
            ? mergeAttachmentClientExtras(
                normalized.attachments,
                current.attachments,
              )
            : (current.attachments || []),
        });
      }
    },
    UPDATE_MESSAGE_CONTENT(state, { tempId, content }) {
      const idx = state.messages.findIndex((m) => m.tempId === tempId);
      if (idx !== -1) {
        Vue.set(state.messages[idx], 'content', content);
      }
    },
    REMOVE_MESSAGE(state, { tempId }) {
      const idx = state.messages.findIndex((m) => m.tempId === tempId);
      if (idx !== -1) {
        state.messages.splice(idx, 1);
      }
    },
    UPDATE_PRODUCT_CARDS_CUTOUT(state, processedProducts) {
      if (!Array.isArray(processedProducts) || !state.parsedPrompt) return;
      const refs = state.parsedPrompt.product_references || [];

      processedProducts.forEach((p) => {
        const ref = refs.find((r) => r.id === p.reference_id);
        if (!ref) return;
        ref.original_url = p.original_url || ref.original_url || ref.value;
        ref.cutout_url = p.cutout_url || null;
        ref.was_auto_cut = Boolean(p.was_auto_cut);
        if (typeof ref.keep_background === 'undefined') {
          ref.keep_background = false;
        }
      });

      // Mirror updates onto any chat message widget arrays so cards re-render
      (state.messages || []).forEach((msg) => {
        (msg.widgets || []).forEach((w) => {
          if (w.type !== 'resolved_product_cards' || !Array.isArray(w.references)) return;
          w.references = w.references.map((wref) => {
            const match = processedProducts.find((p) => p.reference_id === wref.id);
            if (!match) return wref;
            return {
              ...wref,
              original_url: match.original_url || wref.original_url,
              cutout_url: match.cutout_url || null,
              was_auto_cut: Boolean(match.was_auto_cut),
            };
          });
        });
      });
    },
    SET_PRODUCT_KEEP_BACKGROUND(state, { refId, value }) {
      const refs = (state.parsedPrompt && state.parsedPrompt.product_references) || [];
      const ref = refs.find((r) => r.id === refId);
      if (ref) ref.keep_background = Boolean(value);

      (state.messages || []).forEach((msg) => {
        (msg.widgets || []).forEach((w) => {
          if (w.type !== 'resolved_product_cards' || !Array.isArray(w.references)) return;
          w.references = w.references.map((wref) =>
            wref.id === refId ? { ...wref, keep_background: Boolean(value) } : wref
          );
        });
      });
    },
    TOGGLE_PRODUCT_DESELECTED(state, { refId }) {
      const refs = (state.parsedPrompt && state.parsedPrompt.product_references) || [];
      const ref = refs.find((r) => r.id === refId);
      if (ref) ref._deselected = !ref._deselected;

      (state.messages || []).forEach((msg) => {
        (msg.widgets || []).forEach((w) => {
          if (w.type !== 'resolved_product_cards' || !Array.isArray(w.references)) return;
          w.references = w.references.map((wref) =>
            wref.id === refId ? { ...wref, _deselected: !wref._deselected } : wref
          );
        });
      });
    },
  },

  actions: {
    initNetworks({ commit }, rawNetworks) {
      commit('SET_ALL_NETWORKS', normalizeNetworks(rawNetworks));
    },

    async reloadConfig({ commit }, configEndpoint) {
      const { data } = await axios.get(configEndpoint);
      const raw = (data?.networks || []).map((network) => ({
        public_id: network.id,
        db_id: network.db_id,
        name: network.name,
        category: network.category,
        placementTypes: (network.placementTypes || []).map((pt) => ({
          public_id: pt.id,
          db_id: pt.db_id,
          label: pt.label,
          sort_order: Number(pt.sort_order ?? 0),
          sizes: (pt.sizes || []).map((size) => ({
            public_id: size.id,
            db_id: size.db_id,
            name: size.name || '',
            width: Number(size.width || 0),
            height: Number(size.height || 0),
          })),
        })),
      }));
      commit('SET_ALL_NETWORKS', normalizeNetworks(raw));
    },

    async createLayout({ state, commit, dispatch }, createLayoutEndpoint) {
      commit('SET_SAVING', true);

      const LOADING_MESSAGES = [
        'Organizing the pixels…',
        'Briefing the templates…',
        'Aligning the palette…',
        'Warming up the canvas…',
        'Teaching shapes to behave…',
        'Negotiating with whitespace…',
        'Feeding the templates a small snack…',
        'Polishing the corners…',
        'Convincing fonts to cooperate…',
        'Measuring twice, cropping once…',
        'Sprinkling a little magic…',
      ];

      await dispatch('addAndSaveMessage', {
        role: 'assistant',
        content: state.imagePrompt
          ? 'Creating your layout and generating images. This may take a moment...'
          : 'Creating your layout...',
      });

      const funTempId = `tmp_fun_${Date.now()}_${Math.random().toString(36).slice(2)}`;

      commit('ADD_MESSAGE', { role: 'assistant', content: LOADING_MESSAGES[0], tempId: funTempId });

      let cycleIdx = 0;

      const intervalId = setInterval(() => {
        cycleIdx = (cycleIdx + 1) % LOADING_MESSAGES.length;
        commit('UPDATE_MESSAGE_CONTENT', { tempId: funTempId, content: LOADING_MESSAGES[cycleIdx] });
      }, 1200);

      try {
        const pdpImages = (() => {
          const msgs = state.messages || [];
          for (let i = msgs.length - 1; i >= 0; i--) {
            if (msgs[i]._pdpGeneratedImages) return msgs[i]._pdpGeneratedImages;
          }
          return [];
        })();
        const pdpOverlayElements = pdpImages
          .filter((img) => img.overlayText && img.overlayTextMode !== 'part_of_image')
          .map((img) => ({ content: img.overlayText, role: 'headline',
            suggested_color: '#000000', suggested_position: 'top-center',
            suggested_font_size_ratio: 0.08 }));
        const payload = {
          sizesIds: Array.from(state.selectedSizeIds),
          image_prompt: state.imagePrompt,
          background_scene: state.parsedPrompt?.background_scene || null,
          // Resolve any {type:'attachment'} references (e.g. an image attached with
          // the original prompt) into real URLs so the source image actually reaches
          // Gemini instead of arriving as an index the backend can't turn into an image.
          product_references: resolveAttachmentReferences(
            (state.parsedPrompt?.product_references || []).filter((r) => !r._deselected),
            [],
            state.messages || []
          ),
          text_elements: [...(state.parsedPrompt?.text_elements || []), ...pdpOverlayElements],
          selected_background_url: state.selectedBackgroundUrl || null,
          layout_mode: state.layoutMode || 'auto',
          skip_crop: true,
        };
        const { data } = await axios.post(createLayoutEndpoint, payload);
        clearInterval(intervalId);
        if (data?.processed_products) {
          commit('UPDATE_PRODUCT_CARDS_CUTOUT', data.processed_products);
        }
        if (data?.link) {
          // Avoid restoring an "in progress" chat state when user navigates back later.
          dispatch('resetChatSessionLocal');
          window.location.href = data.link;
        }
        return data;
      } catch (e) {
        clearInterval(intervalId);
        console.error('Failed to create layout:', e);
        const msg = e?.response?.data?.message || e?.message || 'Failed to create layout.';
        if (window.toastr) {
          window.toastr.error(msg);
        }
      } finally {
        commit('REMOVE_MESSAGE', { tempId: funTempId });
        commit('SET_SAVING', false);
      }
    },

    async processProductImagesForCutout({ commit }, { endpoint, references }) {
      if (!endpoint || !Array.isArray(references) || references.length === 0) {
        return references || [];
      }
      try {
        const { data } = await axios.post(endpoint, { product_references: references });
        if (data?.processed_products) {
          commit('UPDATE_PRODUCT_CARDS_CUTOUT', data.processed_products);
          return data.processed_products;
        }
      } catch (e) {
        console.error('processProductImagesForCutout failed', e);
      }
      return [];
    },

    resetChatSessionLocal({ commit }) {
      try {
        localStorage.removeItem(CHAT_LOCALSTORAGE_KEY);
        localStorage.removeItem(PENDING_DESIGN_JOBS_KEY);
        localStorage.removeItem(RESOLVED_DESIGN_JOBS_KEY);
        localStorage.removeItem(PARSED_PROMPT_KEY);
      } catch (_) {
        // ignore
      }

      commit('SET_PROCESSING_CHAT_MESSAGE', false);
      commit('SET_SAVING', false);
      commit('SET_IS_CLEARING_CHAT', false);
      commit('SET_CHAT_TEXT', '');
      commit('SET_ATTACHMENTS', []);
      commit('SET_MESSAGES', []);
      commit('SET_PARSED_PROMPT', null);
      commit('SET_PENDING_ATTACH_PROMPT', null);
      commit('SET_BYPASS_ATTACHMENT_CHECK', false);
      commit('SET_SELECTED_BACKGROUND', null);
      commit('CLEAR_REFERENCES');
      commit('CLEAR_TENTPOLE_SEASONAL');
      commit('CLEAR_SELECTION');
      commit('SET_IMAGE_PROMPT', '');
      commit('SET_SELECTED_PLACEMENT', null);
      commit('SET_PENDING_FOLDER_SECTION', null);
      commit('SET_PENDING_BACKGROUND_DESCRIPTION', null);
      commit('SET_PENDING_BACKGROUND_SIZE', null);
      commit('SET_PENDING_SALSIFY_RETRY', null);
      commit('SET_PENDING_TRANSLATE_IMAGE_INTENT', null);
      commit('SET_PENDING_TRANSLATE_IMAGE_WAITING_FOR_IMAGE', null);
      commit('SET_PENDING_DESIGN_JOBS', []);
      commit('SET_RESOLVED_DESIGN_JOBS', []);
      commit('SET_DESCRIBING_PLACEMENT', false);
      commit('SET_PENDING_PDP_INTENT', null);
      commit('SET_PENDING_PDP_REFINE', null);
      commit('SET_PENDING_PDP_REFERENCE', null);
      commit('SET_PENDING_PDP_REFERENCE_ASK', null);
      commit('SET_PENDING_CROSS_SELL_PRODUCT', null);
    },

    clearSelection({ commit }) {
      commit('CLEAR_SELECTION');
    },

    togglePlacement({ commit }, { placement, selected }) {
      placement.sizes.forEach((size) => {
        commit('TOGGLE_SIZE', { sizeId: size.id, selected });
      });
    },

    toggleNetwork({ dispatch }, { network, selected }) {
      getCreatePlacementTypes(network).forEach((pt) => {
        dispatch('togglePlacement', { placement: pt, selected });
      });
    },

    selectAllCreate({ getters, commit }) {
      const ids = [];
      getters.createSections.forEach((section) => {
        section.networks.forEach((network) => {
          getCreatePlacementTypes(network).forEach((pt) => {
            pt.sizes.forEach((s) => ids.push(s.id));
          });
        });
      });
      commit('SELECT_ALL', ids);
    },

    openSizesModal({ commit, state, getters }) {
      commit('SET_SHOW_SIZES_MODAL', true);
    },

    closeSizesModal({ commit }) {
      commit('SET_SHOW_SIZES_MODAL', false);
    },

    async fetchShowcaseTemplates({ commit }, endpoint) {
      const { data } = await axios.get(endpoint);
      commit('SET_SHOWCASE_TEMPLATES', data || []);
      return data || [];
    },
    
    addAttachment({ commit, state, dispatch }, attachment) {
      let normalized = {};

      if (typeof attachment === 'string') {
        normalized = {
          type: 'url',
          payload: attachment,
          preview: attachment,
        };
      } else if (attachment instanceof File) {
        normalized = {
          type: 'file',
          payload: attachment,
          preview: URL.createObjectURL(attachment),
          fileType: attachment.type,
          name: attachment.name,
          size: attachment.size,
          asset_type: attachment?.asset_type ?? null,
          is_external: attachment?.is_external ?? false,
          external_id: attachment?.external_id ?? null,
          download_url: attachment?.download_url ?? null,
          original_path: attachment?.original_path ?? null,
          original_name: attachment?.original_name ?? null,
          display_name: attachment?.display_name ?? null,
          is_da: attachment?.is_da ?? false,
        };
      } else {
        normalized = {
          type: 'file',
          payload: null,
        };
      }

      commit('ADD_ATTACHMENT', normalized);

      // POR-13481: if the missing-attachment guard is waiting for an image, the user
      // just provided one (drag / paste / paperclip / picker) — resume the pending
      // prompt WITH the image so it's used for the banner, instead of a bare
      // "Nice product shot / how would you like to use it?" reply.
      if (state.pendingAttachPrompt) {
        const prompt = state.pendingAttachPrompt;
        commit('SET_PENDING_ATTACH_PROMPT', null);
        commit('SET_CHAT_TEXT', prompt);
        dispatch('sendMessage');
      }
    },

    removeAttachment({ commit, state }, index) {
      const att = state.attachments[index];
      if (att && att.preview) {
        URL.revokeObjectURL(att.preview);
      }

      commit('REMOVE_ATTACHMENT', index);
    },

    clearInput({ commit }) {
      commit('SET_CHAT_TEXT', '');
      commit('SET_ATTACHMENTS', []);
    },

    async sendMessage({ commit, state, dispatch, getters }) {
      // POR-13504: the conversation this send belongs to. If the user clicks
      // "New" (or opens another chat) while the LLM roundtrip / image generation
      // is still in flight, its result must be discarded instead of leaking into
      // the now-active chat. Every message-writing dispatch/commit made from the
      // LLM response onward (including inside the chat-function handlers) goes
      // through `guard`, which no-ops once the active conversation changes.
      const genConversationId = state.activeConversation ? state.activeConversation.id : null;
      const guard = withConversationGuard({ state, commit, dispatch, getters }, genConversationId);

      // POR-13481: deterministic guard for Amazon "A+" banners. The chat model routes
      // an "A+ banner" request non-deterministically (create_template / design concept /
      // create_campaign), so no single downstream handler can reliably tell the user when
      // Amazon isn't set up. "A+" / "A+ Content" is Amazon-specific terminology, so we can
      // detect the intent up front: if the user wants an Amazon A+ creative but Amazon
      // isn't a configured network, say so instead of guessing. Runs before the LLM.
      {
        const aPlusText = String(state.chatText || '');
        const wantsAmazonAplus = /\bA\+\s*(banner|content|module|page|listing|creative)s?\b/i.test(aPlusText)
          || /\bamazon\s+a\+/i.test(aPlusText);
        const hasAmazonNetwork = (state.allNetworks || []).some((n) => /amazon/i.test(n.name || ''));

        if (wantsAmazonAplus && !hasAmazonNetwork) {
          const userText = aPlusText.trim();
          const userAttachments = state.attachments || [];
          if (!userText && !userAttachments.length) {
            return;
          }

          dispatch('clearInput');
          await dispatch('addAndSaveMessage', {
            role: 'user',
            content: userText,
            attachments: userAttachments,
          });

          const available = (state.allNetworks || []).map((n) => n.name).filter(Boolean);
          const availableLine = available.length
            ? ` Your available networks are: <b>${available.join('</b>, <b>')}</b>.`
            : '';
          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: `Amazon A+ isn't set up as a network for your company yet, so I can't create an A+ banner. Please ask your admin to add the Amazon network.${availableLine} Or tell me the exact sizes you'd like (e.g. <b>970x600</b>) and I'll create a custom layout instead.`,
          });
          return;
        }
      }

      // POR-13481: deterministic guard for "use the attached image" with nothing
      // attached. Otherwise the flow silently generates a scene without the user's
      // product (they said "use the attached image" but never attached one). Offer to
      // pick/attach one instead. A one-shot bypass flag lets "Continue without" proceed.
      if (state.bypassAttachmentCheck) {
        commit('SET_BYPASS_ATTACHMENT_CHECK', false);
      } else {
        const attachRefText = String(state.chatText || '');
        const referencesAttachment = /\battached (image|photo|picture|file)\b/i.test(attachRefText)
          || /\bimage (i|that i|we|you)\s+attached\b/i.test(attachRefText);
        const hasAttachment = (state.attachments || []).length > 0;
        const hasResolvedProduct = (state.parsedPrompt?.product_references || [])
          .some((r) => r.type === 'url' || r.type === 'product_asset');

        if (referencesAttachment && !hasAttachment && !hasResolvedProduct) {
          const userText = attachRefText.trim();
          if (!userText) {
            return;
          }

          // Remember the prompt so that attaching/pasting/picking an image next
          // resumes it WITH the image (handled in the addAttachment action) instead
          // of a bare "Nice product shot" reply. (POR-13481)
          commit('SET_PENDING_ATTACH_PROMPT', userText);

          dispatch('clearInput');
          await dispatch('addAndSaveMessage', { role: 'user', content: userText });
          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: "You mentioned using an attached image, but I don't see one attached. Pick an image below, or just paste or attach one (📎) — I'll use it for this banner. Or continue without it.",
            _pendingPrompt: userText,
            widgets: [{
              type: 'action_buttons',
              id: 'missing_attachment',
              buttons: [
                { label: 'Select images', action: 'select_missing_attachment', variant: 'primary' },
                { label: 'Continue without', action: 'skip_missing_attachment', variant: 'secondary' },
              ],
            }],
          });
          return;
        }

        // Proceeding normally — drop any stale pending prompt from a prior guard.
        if (state.pendingAttachPrompt) {
          commit('SET_PENDING_ATTACH_PROMPT', null);
        }
      }

      // POR-13521: the user was asked "what product?" after launching a Library
      // sequence (pdp_pending_sequence set by stageSequenceInCreativeStudio).
      // Turn their reply into product_references and reveal the pre-built row
      // picker directly — do NOT run the normal LLM path for this reply.
      if (state.parsedPrompt && state.parsedPrompt.pdp_pending_sequence) {
        const userText = String(state.chatText || '').trim();
        const userAttachments = state.attachments || [];
        if (!userText && !userAttachments.length) {
          return;
        }

        commit('SET_PROCESSING_CHAT_MESSAGE', true);
        commit('SET_PENDING_PDP_INTENT', null); // clear the product-ask input highlight
        dispatch('clearInput');
        await dispatch('addAndSaveMessage', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });

        const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });

        // An attached image becomes the target product reference directly;
        // otherwise parse the typed text (ASIN / GTIN / name).
        const urlAttachments = [];
        userAttachments
          .filter((a) => a.type === 'url' && a.payload)
          .forEach((a) => urlAttachments.push(a));
        const fileAttachments = userAttachments.filter(
          (a) => a.type === 'file' && a.payload instanceof File
        );
        for (const att of fileAttachments) {
          try {
            const dataUri = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(att.payload);
            });
            if (typeof dataUri === 'string' && dataUri.startsWith('data:')) {
              urlAttachments.push({ ...att, payload: dataUri });
            }
          } catch (e) {
            console.warn('Failed to read product reference attachment', e);
          }
        }

        let productReferences = [];
        if (urlAttachments.length > 0) {
          const first = urlAttachments[0];
          productReferences = [{
            type: 'url',
            value: first.payload,
            display_name: first.display_name || first.name || 'Product image',
            _source: 'attachment_target',
          }];
        } else if (userText) {
          productReferences = parseTypedReference(userText);
        }

        if (!productReferences.length) {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
          return;
        }

        try {
          adapter.setParsedPrompt({
            ...adapter.getParsedPrompt(),
            product_references: productReferences,
          });
          await adapter.saveConversationState();

          const searchable = productReferences.some(
            (r) => r.type === 'name' || r.type === 'gtin' || r.type === 'asin' || r.type === 'salsify'
          );
          if (searchable) {
            await searchAndShowProductImages(productReferences, adapter);
          }
          await revealSequencePickerWithProduct(adapter, productReferences);
        } catch (e) {
          console.error('Failed to reveal sequence picker for typed product reply', e);
          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: 'Something went wrong loading that product. Please try again.',
          });
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }

        return;
      }

      // POR-13321: the user clicked "ASIN" on the PDP reference step and is now
      // typing the reference identifier (or attaching a reference image). Route
      // it deterministically into the reference flow — NOT through the LLM,
      // which reliably reverses the target/reference roles (see POR-12923).
      if (state.pendingPdpReference) {
        const pending = state.pendingPdpReference;
        commit('SET_PENDING_PDP_REFERENCE', null);

        const userText = String(state.chatText || '').trim();
        const userAttachments = state.attachments || [];

        commit('SET_PROCESSING_CHAT_MESSAGE', true);
        dispatch('clearInput');
        await dispatch('addAndSaveMessage', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });

        const productReferences = pending.productReferences
          || state.parsedPrompt?.product_references
          || [];
        const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });

        // If the user attached an image instead of typing an ASIN, treat the
        // attachment(s) as the reference image(s) directly.
        const attachmentUrls = [];
        userAttachments
          .filter((a) => a.type === 'url' && a.payload)
          .forEach((a) => attachmentUrls.push(a.payload));
        const fileAttachments = userAttachments.filter(
          (a) => a.type === 'file' && a.payload instanceof File
        );
        for (const att of fileAttachments) {
          try {
            const dataUri = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(att.payload);
            });
            if (typeof dataUri === 'string' && dataUri.startsWith('data:')) {
              attachmentUrls.push(dataUri);
            }
          } catch (e) {
            console.warn('Failed to read reference attachment', e);
          }
        }

        try {
          if (attachmentUrls.length > 0) {
            await continueWithReferenceImages(adapter, {
              referenceImageUrls: attachmentUrls,
              productReferences,
            });
          } else {
            const referenceProductRefs = parseTypedReference(userText);
            if (referenceProductRefs.length > 0) {
              await fetchReferenceImagesAndContinue(adapter, {
                referenceProductRefs,
                productReferences,
                searchTargetFirst: false,
                useReferenceRowPicker: adapter.supportsReferenceRowPicker(),
              });
            } else {
              // Nothing usable — re-arm and ask again.
              commit('SET_PENDING_PDP_REFERENCE', pending);
              await dispatch('addAndSaveMessage', {
                role: 'assistant',
                content: 'Please enter a reference <b>ASIN</b> (e.g. <i>B07K1K999M</i>) or attach a reference image.',
              });
            }
          }
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }
        return;
      }

      // The "match a style or generate fresh?" ask is showing and the user typed
      // instead of clicking a button. If they pasted an ASIN or an Amazon URL,
      // guess that they meant the "ASIN" reference path and route it as the
      // reference. Non-ASIN text (a description, etc.) clears the ask and flows on
      // to normal handling below. (POR-13612-4)
      if (state.pendingPdpReferenceAsk) {
        const ask = state.pendingPdpReferenceAsk;
        commit('SET_PENDING_PDP_REFERENCE_ASK', null);

        const userText = String(state.chatText || '').trim();
        if (extractAsin(userText)) {
          const productReferences = ask.productReferences
            || state.parsedPrompt?.product_references
            || [];

          commit('SET_PROCESSING_CHAT_MESSAGE', true);
          dispatch('clearInput');
          await dispatch('addAndSaveMessage', {
            role: 'user',
            content: userText,
          });

          const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });
          try {
            await fetchReferenceImagesAndContinue(adapter, {
              referenceProductRefs: parseTypedReference(userText),
              productReferences,
              searchTargetFirst: false,
              useReferenceRowPicker: adapter.supportsReferenceRowPicker(),
            });
          } finally {
            commit('SET_PROCESSING_CHAT_MESSAGE', false);
          }
          return;
        }
        // Not an ASIN/URL — fall through to normal handling.
      }

      // A set with a cross-sell was confirmed and we asked for Product 2 (the
      // cross-sell partner). This typed message is the answer — an uploaded image,
      // or an ASIN/Amazon URL we fetch. Storing pdp_cross_sell_product resumes the
      // stashed set (NetworksPageV2 watches it). (POR-13612-5-2)
      if (state.pendingCrossSellProduct) {
        const userText = String(state.chatText || '').trim();
        const userAttachments = state.attachments || [];

        commit('SET_PROCESSING_CHAT_MESSAGE', true);
        dispatch('clearInput');
        await dispatch('addAndSaveMessage', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });

        let productImage = (userAttachments.find((a) => a.type === 'url' && a.payload) || {}).payload || null;
        const asin = extractAsin(userText);
        if (!productImage && asin) {
          const productRefs = state.parsedPrompt?.product_references || [];
          const marketplace = extractMarketplaceDomain(userText)
            || productRefs.find((r) => r.marketplace)?.marketplace
            || null;
          productImage = await resolveProductOverlayUrl(
            [{ type: 'asin', value: asin, display_name: asin, marketplace }], [],
          );
        }

        if (productImage) {
          // Resumes the stashed set via the NetworksPageV2 watcher.
          commit('SET_PARSED_PROMPT', { pdp_cross_sell_product: { image: productImage } });
        } else {
          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: 'I couldn\'t find that product. Please enter a valid <b>ASIN</b> or Amazon <b>URL</b>, upload an image, or pick one from your library.',
          });
        }
        commit('SET_PROCESSING_CHAT_MESSAGE', false);
        return;
      }

      // Reference-style queue resume: when the PDP queue is paused awaiting
      // a reference image / product image, this user message is the answer.
      // Inject any attachment URLs into parsedPrompt.reference_images_urls,
      // then call tryResumeReferenceQueue. It either resumes (both inputs
      // satisfied) or re-asks (still missing). Skip AI parsing either way.
      const queue = state.parsedPrompt?._pdpQueue;
      if (queue?.awaitingInput?.style === 'reference') {
        const userText = state.chatText || '';
        const userAttachments = state.attachments || [];

        commit('ADD_MESSAGE', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });
        dispatch('clearInput');
        commit('SET_PROCESSING_CHAT_MESSAGE', true);

        // Collect candidate reference URLs from three sources:
        //  1) URL-type attachments (already a URL) — use directly
        //  2) File-type attachments — upload to /upload-temp-file to get a URL
        //  3) URL-shaped tokens inside the user text — extract via regex
        const collectedUrls = [];

        userAttachments
          .filter((a) => a.type === 'url' && a.payload)
          .forEach((a) => collectedUrls.push(a.payload));

        const fileAttachments = userAttachments.filter(
          (a) => a.type === 'file' && a.payload instanceof File
        );
        for (const att of fileAttachments) {
          try {
            // Read the file as a base64 data URI. The backend
            // (NetworksService::createDesignConcept) decodes data: URIs
            // into a local temp file, which avoids the Docker HTTP
            // round-trip that fails for radzs.front URLs.
            const dataUri = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(att.payload);
            });
            if (typeof dataUri === 'string' && dataUri.startsWith('data:')) {
              collectedUrls.push(dataUri);
            }
          } catch (e) {
            console.warn('Failed to read reference attachment', e);
          }
        }

        const urlPattern = /https?:\/\/[^\s<>"']+/gi;
        (userText.match(urlPattern) || []).forEach((u) => collectedUrls.push(u));

        if (collectedUrls.length > 0) {
          const existing = state.parsedPrompt.reference_images_urls || [];
          commit('SET_PARSED_PROMPT', {
            ...state.parsedPrompt,
            reference_images_urls: [...existing, ...collectedUrls],
          });
        }

        const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });

        await tryResumeReferenceQueue(adapter);
        commit('SET_PROCESSING_CHAT_MESSAGE', false);
        return;
      }

      if (queue?.awaitingInput?.style === 'gs1_describe') {
        const userText = state.chatText || '';
        const userAttachments = state.attachments || [];

        commit('ADD_MESSAGE', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });
        dispatch('clearInput');
        commit('SET_PROCESSING_CHAT_MESSAGE', true);

        const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });

        await tryResumeGs1DescribeQueue(adapter, userText);
        commit('SET_PROCESSING_CHAT_MESSAGE', false);
        return;
      }

      if (queue?.awaitingInput?.style === 'gs1_reference') {
        const userText = state.chatText || '';
        const userAttachments = state.attachments || [];

        commit('ADD_MESSAGE', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });
        dispatch('clearInput');
        commit('SET_PROCESSING_CHAT_MESSAGE', true);

        // Reuse the same multi-source URL extractor we use for Mobile Hero
        // Reference: URL-type attachments → directly; File-type → base64
        // data URI; URL-shaped tokens in the text → regex-extracted.
        const collectedUrls = [];
        userAttachments
          .filter((a) => a.type === 'url' && a.payload)
          .forEach((a) => collectedUrls.push(a.payload));
        const fileAttachments = userAttachments.filter(
          (a) => a.type === 'file' && a.payload instanceof File
        );
        for (const att of fileAttachments) {
          try {
            const dataUri = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(att.payload);
            });
            if (typeof dataUri === 'string' && dataUri.startsWith('data:')) {
              collectedUrls.push(dataUri);
            }
          } catch (e) {
            console.warn('Failed to read GS1 reference attachment', e);
          }
        }
        const urlPattern = /https?:\/\/[^\s<>"']+/gi;
        (userText.match(urlPattern) || []).forEach((u) => collectedUrls.push(u));

        const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });

        await tryResumeGs1ReferenceQueue(adapter, collectedUrls);
        commit('SET_PROCESSING_CHAT_MESSAGE', false);
        return;
      }

      // Parallel-grid up-front prep equivalents of the sequential GS1 sub-flow.
      // These fire ONLY while the parallel prep is paused awaiting GS1 input
      // (kind === 'gs1_describe' / 'gs1_reference'); when _pdpParallelPrep is
      // absent — the normal case — neither branch runs and the send action
      // proceeds exactly as before (general chat, sequential queue, etc.).
      const prep = state.parsedPrompt && state.parsedPrompt._pdpParallelPrep;

      if (prep?.awaitingInput?.kind === 'gs1_describe') {
        const userText = state.chatText || '';
        const userAttachments = state.attachments || [];

        commit('ADD_MESSAGE', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });
        dispatch('clearInput');
        commit('SET_PROCESSING_CHAT_MESSAGE', true);

        const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });

        await tryResumeGs1DescribeForPrep(adapter, userText);
        commit('SET_PROCESSING_CHAT_MESSAGE', false);
        return;
      }

      if (prep?.awaitingInput?.kind === 'gs1_reference') {
        const userText = state.chatText || '';
        const userAttachments = state.attachments || [];

        commit('ADD_MESSAGE', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });
        dispatch('clearInput');
        commit('SET_PROCESSING_CHAT_MESSAGE', true);

        // Same multi-source URL extraction as the sequential gs1_reference
        // branch: URL-type attachments → directly; File-type → base64 data
        // URI; URL-shaped tokens in the text → regex-extracted.
        const collectedUrls = [];
        userAttachments
          .filter((a) => a.type === 'url' && a.payload)
          .forEach((a) => collectedUrls.push(a.payload));
        const fileAttachments = userAttachments.filter(
          (a) => a.type === 'file' && a.payload instanceof File
        );
        for (const att of fileAttachments) {
          try {
            const dataUri = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(reader.error);
              reader.readAsDataURL(att.payload);
            });
            if (typeof dataUri === 'string' && dataUri.startsWith('data:')) {
              collectedUrls.push(dataUri);
            }
          } catch (e) {
            console.warn('Failed to read GS1 prep reference attachment', e);
          }
        }
        const urlPattern = /https?:\/\/[^\s<>"']+/gi;
        (userText.match(urlPattern) || []).forEach((u) => collectedUrls.push(u));

        const adapter = createCreativeStudioPdpContextAdapter({ state, commit: guard.commit, dispatch: guard.dispatch });

        await tryResumeGs1ReferenceForPrep(adapter, collectedUrls);
        commit('SET_PROCESSING_CHAT_MESSAGE', false);
        return;
      }

      if (state.pendingFolderSection) {
        const { section, layoutFoldersUrl } = state.pendingFolderSection;

        const folderName = state.chatText.trim();

        if (!folderName) {
          return;
        }

        commit('SET_PENDING_FOLDER_SECTION', null);
        commit('SET_PROCESSING_CHAT_MESSAGE', true);
        dispatch('clearInput');

        const sectionLabel = section.charAt(0).toUpperCase() + section.slice(1);

        await dispatch('addAndSaveMessage', { role: 'user', content: folderName });
        await dispatch('addAndSaveMessage', {
          role: 'assistant',
          content: `Creating folder "<b>${folderName}</b>" in <b>${sectionLabel}</b>…`,
        });
        try {
          let folderId;

          if (section === 'layouts') {
            const { data } = await axios.post('/banner/layout_folder_create', { parent_id: null, name: folderName });
            folderId = data.id;

            const hash = '#layout-folders/' + folderId;

            sessionStorage.setItem('newLayoutFolder', JSON.stringify({
              hash: hash
            }));

            window.open(layoutFoldersUrl + hash, '_blank');
          } else {
            const { data } = await axios.post('/file/create_folder_by_name', { parent_id: null, name: folderName, tab: section });
            folderId = data.id;
            window.open('/file/browser/' + section + '/' + folderId, '_blank');
          }

          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: `Successfully created folder "<b>${folderName}</b>" in <b>${sectionLabel}</b>.`,
          });
        } catch (e) {
          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: 'Sorry, folder creation failed. Please try again.',
          });
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }

        return;
      }


      if (state.pendingBackgroundDescription) {
        const userText = state.chatText.trim();

        const fallback = state.pendingBackgroundDescription === '__new__'
          ? 'Clean, professional product background with soft lighting and subtle gradient'
          : state.pendingBackgroundDescription;

        const newScene = userText || fallback;

        const pendingSize = state.pendingBackgroundSize;

        commit('SET_PENDING_BACKGROUND_DESCRIPTION', null);
        commit('SET_PENDING_BACKGROUND_SIZE', null);

        await dispatch('addAndSaveMessage', { role: 'user', content: userText || '(auto-generate background)' });
        dispatch('clearInput');

        commit('SET_PARSED_PROMPT', { ...(state.parsedPrompt || {}), background_scene: newScene });

        const pdpSize = pendingSize || state.parsedPrompt?.pdp_size || null;

        generateAndShowBackgroundPreviews(newScene, { commit: guard.commit, dispatch: guard.dispatch }, pdpSize);

        return;
      }

      if (state.pendingSalsifyRetry) {
        const salsifyId = state.chatText.trim();

        if (!salsifyId) {
          return;
        }

        commit('SET_PENDING_SALSIFY_RETRY', null);
        dispatch('clearInput');

        await dispatch('addAndSaveMessage', { role: 'user', content: salsifyId });

        const searchOk = await searchAndShowProductImages(
          [{ type: 'salsify', value: salsifyId, display_name: salsifyId }],
          { commit: guard.commit, dispatch: guard.dispatch }
        );

        if (searchOk !== false && state.parsedPrompt?.pdp_is_set !== undefined) {
          const isSet = state.parsedPrompt.pdp_is_set;

          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: isSet ? 'How would you like to create your PDP image set?' : 'How would you like to create your PDP image?',
            widgets: [{
              type: 'action_buttons',
              id: 'pdp_creation_path',
              buttons: [
                { label: 'AI Static Image', iconClass: 'bi bi-stars', action: 'pdp_path:ai_generate', variant: 'primary' },
                { label: 'AI Layered Image', iconClass: 'bi bi-layers', action: 'pdp_path:layered_image', variant: 'primary' },
                { label: 'From Template', iconClass: 'bi bi-grid', action: 'pdp_path:template', variant: 'primary' },
              ],
            }],
          });
        }

        return;
      }

      const psdAttachment = state.attachments.find(
        (a) => a.type === 'file' && a.name?.toLowerCase().endsWith('.psd')
      );

      if (psdAttachment) {
        commit('SET_PENDING_PSD_IMPORT', false);

        const userText = state.chatText.trim() || 'Import PSD';

        await dispatch('addAndSaveMessage', {
          role: 'user',
          content: userText
        });

        dispatch('clearInput');

        commit('SET_PROCESSING_CHAT_MESSAGE', true);

        try {
          const uploadForm = new FormData();

          uploadForm.append('file', psdAttachment.payload);

          const { data: uploadData } = await axios.post('/upload-temp-file', uploadForm, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          localStorage.setItem('import-from-preselected-psd', JSON.stringify({
            files: [{ url: uploadData.url, name: uploadData.name }],
          }));

          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: 'Creating a new layout and importing your PSD\u2026',
          });

          const { data: layoutData } = await axios.post('/banner/0/group/create-layout-from-psd', {
            file: { name: uploadData.name },
          });
          
          let layoutUrl = `/banner/${layoutData.customer_id}/group/${layoutData.layout_id}/show?action=import-from-preselected-psd`;

          
          if (state.selectedSizeIds.size > 0) {
            const selectedSizesIds = Array.from(state.selectedSizeIds);
            const selectedSizes = [];
            for (const network of state.allNetworks) {
              if (!network.placementTypes) {
                continue;
              }

              for (const placementType of network.placementTypes) {
                const sizes = placementType.sizes
                  .filter(size => selectedSizesIds.includes(size.db_id))
                  .map(size => ({
                    width: size.width,
                    height: size.height,
                  }));

                selectedSizes.push(...sizes);
              }
            }
            
            localStorage.setItem('post-action:resize', JSON.stringify({ sizes: selectedSizes }));
            layoutUrl += `&post-actions=resize`;
          }
          window.location.href = layoutUrl;
        } catch (e) {
          const msg = e?.response?.data?.message || 'Failed to import PSD. Please try again.';

          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: msg
          });

        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }
        return;
      }

      if (state.pendingPsdImport) {
        const userText = state.chatText.trim();

        if (userText) {
          await dispatch('addAndSaveMessage', {
            role: 'user',
            content: userText
          });
        }

        dispatch('clearInput');

        await dispatch('addAndSaveMessage', {
          role: 'assistant',
          content: 'Still waiting for a PSD file. Please attach one and send.',
        });

        return;
      }

      // Resume the PDP fast-path: a previous turn parked a "what product?"
      // question (pendingPdpIntent) and this reply carries the product info.
      // Skip the OpenAI roundtrip — call handleCreatePdpImageSet directly with
      // a synthetic function_call so the path picker shows immediately.
      if (state.pendingPdpIntent) {
        const intent = state.pendingPdpIntent;
        commit('SET_PENDING_PDP_INTENT', null);

        const userText = String(state.chatText || '').trim();
        const userAttachments = state.attachments || [];

        commit('SET_PROCESSING_CHAT_MESSAGE', true);
        dispatch('clearInput');

        const savedReply = await dispatch('addAndSaveMessage', {
          role: 'user',
          content: userText,
          attachments: userAttachments,
        });

        const productRefs = [];
        // Accept a bare ASIN OR a full Amazon product URL (extract the ASIN from
        // it) so a pasted URL resolves the real product instead of being treated as
        // a free-text name ("Got <the whole URL>", never fetched). (POR-13612-2)
        const asinFromInput = extractAsin(userText);
        if (asinFromInput) {
          // Keep the URL's marketplace domain so a non-US ASIN is fetched on its own
          // storefront (amazon.com.mx, etc.); null for a bare ASIN. (POR-13612-2)
          productRefs.push({
            type: 'asin',
            value: asinFromInput,
            display_name: asinFromInput,
            marketplace: extractMarketplaceDomain(userText),
          });
        } else if (/^\d{12,14}$/.test(userText)) {
          productRefs.push({ type: 'gtin', value: userText, display_name: userText });
        } else if ((savedReply.attachments || []).length > 0) {
          // resolveAttachmentReferences will pull the URL from the user message
          // we just persisted (lastUserMsg fallback path).
          productRefs.push({
            type: 'attachment',
            value: '0',
            display_name: savedReply.attachments[0]?.name || 'Attached product image',
          });
        } else if (userText) {
          productRefs.push({ type: 'name', value: userText, display_name: userText });
        }

        const syntheticMessage = {
          function_call: {
            name: 'create_pdp_image_set',
            arguments: JSON.stringify({
              is_set: !!intent.isSet,
              product_references: productRefs,
              image_types: [],
            }),
          },
          savedAttachments: [],
        };

        try {
          const handler = CHAT_FUNCTION_HANDLERS['create_pdp_image_set'];
          if (handler) {
            await handler(syntheticMessage, { state, commit: guard.commit, dispatch: guard.dispatch, getters });
          }
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }
        return;
      }

      if (state.pendingTranslateImageIntent) {
        const userText = String(state.chatText || '').trim();

        commit('SET_PROCESSING_CHAT_MESSAGE', true);
        dispatch('clearInput');

        await dispatch('addAndSaveMessage', { role: 'user', content: userText });

        const languages = userText
          .split(/,|\band\b|\bor\b|;/i)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => s.charAt(0).toUpperCase() + s.slice(1));

        const syntheticTranslateMessage = {
          function_call: {
            name: 'translate_image',
            arguments: JSON.stringify({ target_languages: languages }),
          },
          savedAttachments: [],
        };

        try {
          const handler = CHAT_FUNCTION_HANDLERS['translate_image'];

          if (handler) {
            await handler(syntheticTranslateMessage, { state, commit: guard.commit, dispatch: guard.dispatch, getters });
          }
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }

        return;
      }

      if (state.pendingTranslateImageWaitingForImage) {
        const imageAtt = (state.attachments || []).find(
          (a) => a.type === 'file' || a.type === 'url'
        );

        if (imageAtt) {
          const intent = state.pendingTranslateImageWaitingForImage;
          const userText = String(state.chatText || '').trim();

          commit('SET_PENDING_TRANSLATE_IMAGE_WAITING_FOR_IMAGE', null);
          commit('SET_PROCESSING_CHAT_MESSAGE', true);
          dispatch('clearInput');

          await dispatch('addAndSaveMessage', {
            role: 'user',
            content: userText,
            attachments: [imageAtt],
          });

          const syntheticMessage = {
            function_call: {
              name: 'translate_image',
              arguments: JSON.stringify({ target_languages: intent.targetLanguages }),
            },
            savedAttachments: [],
          };

          try {
            const handler = CHAT_FUNCTION_HANDLERS['translate_image'];

            if (handler) {
              await handler(syntheticMessage, { state, commit: guard.commit, dispatch: guard.dispatch, getters });
            }
          } finally {
            commit('SET_PROCESSING_CHAT_MESSAGE', false);
          }

          return;
        }

        commit('SET_PENDING_TRANSLATE_IMAGE_WAITING_FOR_IMAGE', null);
      }

      commit('SET_PROCESSING_CHAT_MESSAGE', true);

      const newMessage = {
        role: "user",
        content: state.chatText,
        attachments: state.attachments
      };

      dispatch('clearInput');

      const savedUserMessage = await dispatch('addAndSaveMessage', newMessage);

      // Fast-path: when the user submits one of the PDP hero-card phrases with
      // no attachments and no product context anywhere in the message, skip the
      // OpenAI roundtrip and ask for product info immediately. The same guard
      // exists inside handleCreatePdpImageSet as a defensive fallback, but
      // resolving it here avoids the ~2s call-open-ai delay the user would
      // otherwise see for an empty PDP request.
      const trimmedText = String(savedUserMessage.content || '').trim().toLowerCase();

      const PDP_TRIGGER_PHRASES = new Set([
        'create a pdp image',
        'create a pdp image set',
      ]);

      const hasAnyAttachment = (savedUserMessage.attachments || []).length > 0;

      if (PDP_TRIGGER_PHRASES.has(trimmedText)) {
        const isSet = trimmedText === 'create a pdp image set';

        if (!hasAnyAttachment) {
          const noun = isSet ? 'PDP image set' : 'PDP image';
          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content:
              `What product would you like to create a ${noun} for? ` +
              'You can enter an ASIN, Salsify ID, GTIN, attach a reference image, or describe what you want.',
            widgets: [{
              type: 'action_buttons',
              id: 'pdp_from_brief',
              buttons: [{ label: 'Or start from a brief →', action: 'from_brief_open', variant: 'light' }],
            }],
          });
          // Park the intent so the user's next reply resumes the PDP flow
          // synthetically (see the pendingPdpIntent branch above).
          commit('SET_PENDING_PDP_INTENT', { isSet });
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
          return;
        }

        // Fast-path: known PDP phrase + Attachment → skip OpenAI, call handler directly
        const syntheticMessage = {
          function_call: {
            name: 'create_pdp_image_set',
            arguments: JSON.stringify({
              is_set: isSet,
              product_references: [],
              image_types: [],
              image_prompt: String(savedUserMessage.content || ''),
            }),
          },
          savedAttachments: savedUserMessage.attachments || [],
        };
        try {
          const handler = CHAT_FUNCTION_HANDLERS['create_pdp_image_set'];
          if (handler) {
            await handler(syntheticMessage, { state, commit: guard.commit, dispatch: guard.dispatch, getters });
          }
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }
        return;
      }

      // Fast-path: known PDP phrase + ASIN/GTIN → skip OpenAI, call handler directly
      // Prefer deterministic extraction (a full Amazon URL — any /dp/, /gp/product/,
      // ?ASIN=, sspa… form — or a bare ASIN); fall back to an embedded B+9 token for
      // natural-language prompts. (POR-13612-2)
      const pdpAsin = extractAsin(savedUserMessage.content)
        || (savedUserMessage.content?.match(/\bB[A-Z0-9]{9}\b/i)?.[0]?.toUpperCase() || null);
      const pdpGtinMatch = savedUserMessage.content?.match(/\b\d{12,14}\b/);

      if ((pdpAsin || pdpGtinMatch) && [...PDP_TRIGGER_PHRASES].some((p) => trimmedText.includes(p))) {
        const productRef = pdpAsin
          ? { type: 'asin', value: pdpAsin, display_name: pdpAsin }
          : { type: 'gtin', value: pdpGtinMatch[0], display_name: pdpGtinMatch[0] };

        const isSet = trimmedText.includes('image set');

        const syntheticMessage = {
          function_call: {
            name: 'create_pdp_image_set',
            arguments: JSON.stringify({
              is_set: isSet,
              product_references: [productRef],
              image_types: [],
              image_prompt: String(savedUserMessage.content || ''),
            }),
          },
          savedAttachments: savedUserMessage.attachments || [],
        };
        try {
          const handler = CHAT_FUNCTION_HANDLERS['create_pdp_image_set'];

          if (handler) {
            await handler(syntheticMessage, { state, commit: guard.commit, dispatch: guard.dispatch, getters });
          }
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }
        return;
      }

      // Fast-path: hero-card titles map 1:1 to a network, so skip the LLM roundtrip.  
      const CAMPAIGN_TRIGGER_PHRASES = {
        'create a walmart connect campaign': 'Walmart Connect',
        'instacart ads placement set':       'Instacart',
        'new meta set':                      'Meta',
      };

      if (CAMPAIGN_TRIGGER_PHRASES[trimmedText] && !hasAnyAttachment) {
        commit('SET_PENDING_PLACEMENT_DESCRIBE', true);

        const syntheticMessage = {
          function_call: {
            name: 'create_campaign',
            arguments: JSON.stringify({ network_name: CAMPAIGN_TRIGGER_PHRASES[trimmedText] }),
          },
          savedAttachments: savedUserMessage.attachments || [],
        };

        try {
          const handler = CHAT_FUNCTION_HANDLERS['create_campaign'];
          if (handler) {
            await handler(syntheticMessage, { state, commit: guard.commit, dispatch: guard.dispatch, getters });
          }
        } catch (e) {
          console.error('Fast-path create_campaign failed:', e);
          await dispatch('addAndSaveMessage', {
            role: 'assistant',
            content: 'Something went wrong. Please try again.',
          });
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }
        return;
      }

      // Fast-path: the A/B hero experiment tile maps 1:1 to the form widget. (POR-13385)
      if (trimmedText === 'generate two hero images and a/b test them using optix') {
        const syntheticMessage = { function_call: { name: 'generate_ab_hero_test', arguments: '{}' } };
        try {
          const handler = CHAT_FUNCTION_HANDLERS['generate_ab_hero_test'];
          if (handler) await handler(syntheticMessage, { state, commit: guard.commit, dispatch: guard.dispatch, getters });
        } finally {
          commit('SET_PROCESSING_CHAT_MESSAGE', false);
        }
        return;
      }

      const formData = new FormData();

      const lastImg = getters.lastGeneratedImage;
      const visibleAttachments = savedUserMessage.attachments || [];
      const allAttachmentsForApi = [...visibleAttachments];

      // Only feed the previously generated image to the model when the user
      // actually refers to it (e.g. "add that image to a layout", "edit this
      // render"). Injecting it unconditionally made the model reuse a prior
      // composite as the "background" for unrelated prompts like "place X on a
      // kitchen counter", stacking products onto the old result. Reference to a
      // "product" does NOT count — only references to a prior visual output.
      const userTextForImageRef = String(savedUserMessage.content || '');
      const referencesPriorImage = /\b(?:this|that|the|last|previous|prior|above|generated|latest|earlier)\s+(?:image|images|render|renders|result|results|photo|photos|picture|pictures|design|designs|concept|concepts|version|versions|output|composite|generation|mockup)\b/i.test(userTextForImageRef);

      if (lastImg && referencesPriorImage && !visibleAttachments.some(a => a.type === 'url' && a.payload === lastImg)) {
        allAttachmentsForApi.push({ type: 'url', payload: lastImg });
      }

      const networkNames = state.allNetworks.map(n => n.name).join(', ');

      formData.append(
        'messages',
        JSON.stringify([
          {
              role: "system",
              content: `You are a Creative Studio assistant. You help users with seven types of tasks:

1. **Image editing / PDP images**: If the user wants to edit an image, score an image, create a 3D render, do a photoshoot, do a virtual photoshoot, or place an image in a lifestyle setting, use the redirect_to_pdp_images function with the appropriate import_type.

2. **Campaign creation**: If the user wants to create a banner/campaign for a specific retailer, ad network, or platform, use the create_campaign function with that network name — even if the network is NOT in this company's available networks (${networkNames}). Do NOT fall back to create_template just because the referenced network is missing from that list; the app checks availability and tells the user if it still needs to be set up. Note: "A+", "A+ Banner", "A+ Content", and "A+ page" ALWAYS refer to the Amazon network.

3. **Custom size templates**: Use create_template when the user explicitly asks to create templates/layouts in specific pixel sizes such as "300x250", "728x90", or similar, OR when the user says "Add that image to a new layout". Do NOT use create_template when the user references a retailer, ad network, or platform (e.g. "A+ Banner", "Amazon", "Walmart") — use create_campaign for those instead (see rule 2), even if that network is not in the available list. If no resolution or size is specified, use 1024x1024 as the default. If the user wants to use an existing image as the base for a new layout (e.g., when referencing 'that image' or 'this image'), you MUST NOT provide a background_scene or image_prompt. Do NOT use create_template for general design ideas, visual concepts, or generated design images.

4. **Browse templates**: If the user wants to see available templates, browse the template gallery, or asks "show me templates", use the browse_templates function.

5. **Design concept generation**: Use generate_design_concept when the user wants only a visual concept, design idea, ad concept, creative direction, or standalone generated design image. Do this when they are asking for an image only, not for campaign creation, and not for layout/template creation.

6. **Download image**: If the user asks to "download", "save", "download this image", or "grab the last image", use the download_last_image function. Do not use this for general help requests, only for downloading generated visual content.

7. **PDP image creation**: If the user wants to create PDP images, a PDP image set, product detail page images, "how to" images, "reason to believe" images, "hero" images, "lifestyle" images, "cross-sell" images, "product gallery" images, "category gallery" images, "feature & iconography" images, or similar product page content, use create_pdp_image_set. Extract image types mentioned (hero, how_to, reason_to_believe, lifestyle, comparison, ingredients, features, product_gallery, cross_sell, category_gallery, feature_iconography_around, feature_iconography_bottom). Map phrasing to keys: "cross-sell" -> cross_sell; "product gallery" -> product_gallery; "category gallery" -> category_gallery; "feature & iconography around the product" / "benefit icons around the product" -> feature_iconography_around; "feature & iconography at the bottom" / "benefit icons below the image" -> feature_iconography_bottom. Extract product references (ASIN, GTIN, product name, attachments). For the image_prompt field, capture the full descriptive intent of the user, including specific instructions like "showing a 3-step process for using the pen" or specific visual details. If the user provides an ASIN (B followed by 9 alphanumeric characters), use type "asin". If the user provides an Amazon product URL (containing /dp/BXXXXXXXXX/), extract the ASIN and use type "asin".

8. **A/B hero experiment**: When the user wants TWO hero image variations from one source product to A/B test (e.g. "generate two hero images and A/B test them", "two hero variants to test in Optix"), use the generate_ab_hero_test function.

This includes vague prompts such as:
- "Give me a design idea"
- "Show me a concept"
- "Give me a creative direction"
- "Generate an ad idea"
- "Show me a design"
- "Make me a design"

Important routing rules:
- PDP image sets / product page images: use create_pdp_image_set.
- Flipbook templates from the gallery: use redirect_to_design_gallery with type_filter "flipbooks".
- If the user wants only a design/image/concept, ALWAYS use generate_design_concept.
- Do NOT use create_template unless the user explicitly asks for specific sizes or explicitly asks to create a template/layout.
- Do NOT use create_campaign unless the user explicitly wants a campaign created in the app.
- If a request is vague and sounds like a creative/image request, prefer generate_design_concept.
- If the user mentions "layout" or "new layout", use create_template or create_campaign.
- Two hero variants to A/B test / compare in Optix: use generate_ab_hero_test (NOT create_pdp_image_set).

- If the user asks to import a PSD, import from Photoshop, or mentions importing a PSD file, call import_psd. Do not reply with plain text — always use the function.

- If the user asks to create, make, open, start, or launch a sell sheet, call open_sell_sheet.
- Do not reply with plain text saying you are opening it.
- Always use the function.
- Examples:
  - "Create sell sheet"
  - "Make a sell sheet"
  - "Open sell sheet"
  - "Start a sell sheet"

Size rules for generate_design_concept:
- Use exactly one size.
- By default, use size 400x300.
- If the request is for Instacart, use size 3000x800 instead.
- If the user explicitly provides a size, use that size instead.
- If the user requests one design concept, use image_prompt.
- If the user requests multiple distinct concept types in one message, use image_prompts.
- Example:
  - "Create a design concept for a Batiste shampoo hero image" => one prompt
  - "Create a design concept for a Batiste shampoo hero image and a how to use image" => two prompts in image_prompts

Image usage rules for generate_design_concept:
- If the user has attached images, they may be used as visual references, inspiration, style cues, composition references, or product references.
- Reference images and product images are NOT the same:
  - **reference images** = inspiration, style, composition, mood, background, layout cues
  - **product images** = the actual products that should appear in the generated design
- If the user says things like "use this product", "using this product", "feature this product", "using this packshot", or mentions a specific product image filename (e.g., "00028400096348.png"), a GTIN (12-14 digits), or an ASIN (10-character alphanumeric identifier starting with 'B'), treat that as a product image, not just a reference image.
- If the user mentions one or more product image filenames, GTINs, or ASINs, include them in product_image_names exactly as written.
- If attached images are being used only for inspiration/style, set use_attachments_as_reference to true.
- If attached images or mentioned filenames are the actual products to appear in the design, set use_product_images to true.
- If both product images and reference images are relevant, use both.
- Prefer preserving explicitly mentioned product filenames exactly as written.
- If product images are provided, they are the actual featured products that must appear in the final design.
- Do not reinterpret, redesign, or replace provided product images with generic generated packaging.
- Preserve the visible packaging identity, colors, and branding appearance of provided product images as closely as possible.
- If both product images and reference images are present, use product images as the hero products and use reference images only for layout, composition, mood, typography zones, or background inspiration.

Examples:
- "Give me a design idea for Doritos" -> generate_design_concept
- "Show me a concept for a fall soccer snack ad" -> generate_design_concept
- "Create a 300x250 and 728x90 banner" -> create_template
- "Add this image to a new layout" -> create_template with attachment index
- "Create a Walmart Connect campaign" -> create_campaign
- "Edit this product image" -> redirect_to_pdp_images
- "Create a Walmart Connect design concept for a soccer campaign using this product: 00028400096348.png" -> generate_design_concept with use_product_images=true and product_image_names=["00028400096348.png"]
- "Design concept using product B0BTMTTXBF" -> generate_design_concept with use_product_images=true and product_image_names=["B0BTMTTXBF"]
- "Add that image to a new layout" -> create_template
- "Create a PDP image set" -> create_pdp_image_set with is_set=true
- "Create an image set" -> create_pdp_image_set with is_set=true
- "Create multiple images" -> create_pdp_image_set with is_set=true
- "Create multiple PDP images for this product" -> create_pdp_image_set with is_set=true
- "Make product detail page images" -> create_pdp_image_set with is_set=true
- "Create a how-to image for Batiste shampoo showing the foam application" -> create_pdp_image_set with is_set=false, image_types=["how_to"], product_references=[{type:"name", value:"Batiste shampoo", display_name:"Batiste shampoo"}], image_prompt="Create a how-to image for Batiste shampoo showing the foam application"
- "Create a hero image, a reason to believe image, and a how to use image for B0BTMTTXBF showing a 3 step process" -> create_pdp_image_set with is_set=true, image_types=["hero","reason_to_believe","how_to"], product_references=[{type:"asin", value:"B0BTMTTXBF", display_name:"B0BTMTTXBF"}], image_prompt="Create a hero image, a reason to believe image, and a how to use image for B0BTMTTXBF showing a 3 step process"
- "Create a cross-sell image for B0010XRZ7W" -> create_pdp_image_set with is_set=false, image_types=["cross_sell"], product_references=[{type:"asin", value:"B0010XRZ7W", display_name:"B0010XRZ7W"}], image_prompt="Create a cross-sell image for B0010XRZ7W"
- "Create a feature & iconography image around the product for B0BTMTTXBF" -> create_pdp_image_set with is_set=false, image_types=["feature_iconography_around"], product_references=[{type:"asin", value:"B0BTMTTXBF", display_name:"B0BTMTTXBF"}], image_prompt="Create a feature & iconography image around the product for B0BTMTTXBF"
- "Create a PDP image for this shampoo" -> create_pdp_image_set with is_set=false, product_references from context, image_prompt="Create a PDP image for this shampoo"
- "Create a PDP image for B0BTMTTXBF" -> create_pdp_image_set with is_set=false, product_references=[{type:"asin", value:"B0BTMTTXBF", display_name:"B0BTMTTXBF"}]
- "Create a PDP image set for ASIN B01 based on ASIN B07" -> create_pdp_image_set with is_set=true, product_references=[{type:"asin", value:"B01"}], reference_product_references=[{type:"asin", value:"B07"}] (target = B01 before "based on"; reference = B07 after "based on")
- "Create a PDP image set for this product <attached> based on ASIN B07" -> create_pdp_image_set with is_set=true, product_references=[{type:"attachment", value:"0"}], reference_product_references=[{type:"asin", value:"B07"}], use_attachments_as_reference=false (the attached image is the TARGET; the ASIN after "based on" is the REFERENCE)

8. **Image translation**: If the user wants to translate an image to one or more languages, call translate_image. Extract language names from the message into target_languages. If no languages are specified (including when the user writes only "translate", "translate image", "translate it", "translate this", etc.), call translate_image with an empty target_languages array — the handler will ask the user for languages. Do NOT respond with plain text when a translation request is detected — always call translate_image. This applies even when the message is very short (one or two words) as long as it expresses translation intent and an image is attached.

Trigger words/phrases (always call translate_image, never plain text):
- "translate" / "translate image" / "translate this" / "translate it" / "translate please"
- "translate to [language]" / "translate into [language]"
- "translate this image" / "translate this image to [language(s)]"
- Any phrasing where the user wants image text converted to another language

Examples:
- "translate" (with attachment) -> translate_image with target_languages=[]
- "translate image" -> translate_image with target_languages=[]
- "translate this" -> translate_image with target_languages=[]
- "translate to Spanish" -> translate_image with target_languages=["Spanish"]
- "Translate this image to French" -> translate_image with target_languages=["French"]
- "Translate this image to French, German and Spanish" -> translate_image with target_languages=["French","German","Spanish"]
- "Translate this image" -> translate_image with target_languages=[]
- "Translate these images" (multiple attachments) -> translate_image; the handler will inform the user only one image is supported at a time


**Layered template decomposition rules (for create_campaign and create_template):**
When using create_campaign or create_template, you MUST decompose the user's request into structured fields:
- **background_scene**: Describe ONLY the scene, environment, mood, colors, and atmosphere. NEVER include product names, brand names, or specific product imagery. Example: user says "Doritos on a summer beach" -> background_scene = "A sunny summer beach scene with golden sand, blue ocean waves, and bright tropical colors". IMPORTANT: Omit background_scene if the user wants to use an existing image as the base for the layout.
- **product_references**: Extract product identifiers from the prompt. If the user mentions a brand name (e.g., "Doritos", "Lays"), use type "name" with the brand name as value. If they provide a GTIN/UPC number (12-14 digits), use type "gtin". If they provide an ASIN (10-character alphanumeric identifier starting with 'B'), use type "asin". If they attached an image to use as a product, use type "attachment" with the attachment index as value. If the user refers to 'that image' or 'the last image', use type 'last_generated'. CRITICAL: the 'value' and 'display_name' fields must contain ONLY the bare identifier (e.g., "B07MGZ6X2Q", "Doritos", "00028400096348") — NEVER the full user prompt. The full prompt belongs in 'image_prompt'. Wrong: value of "Create a PDP image for B07MGZ6X2Q". Right: value of "B07MGZ6X2Q".
- **text_elements**: ONLY include text if the user EXPLICITLY mentioned specific text content (e.g., 'with text "Buy Now"'). Do NOT invent, add, or generate any text that the user did not specifically request. If the user's prompt does not contain quoted text or explicit text instructions, leave text_elements as an empty array.
- For text_elements, suggest appropriate position (contrasting with the scene), color, and font size ratio based on the text role (headline ~0.08, subheadline ~0.05, cta ~0.06, body ~0.03).

**Browser redirection rules:**
- If the user asks to upload, add, import, save, or store files/attachments/documents/assets/images, call open_file_browser_files.
- If the user asks to upload products, upload product images, or similar, call open_file_browser_products.
- Do not respond with plain text saying where to go. Use the function.

**FILE UPLOAD RULES**
- If the user asks to upload files and the most recent user message has no attachments, call open_file_browser_files.
- If the user asks to upload files and the most recent user message has attachments, call upload_chat_attachments_to_folder with mode "files" and the attachment indexes from the most recent user message.
- Do not call open_file_browser_files when the most recent user message already contains attachments.
- When attachments are present, never respond with:
  - "Name a folder"
  - "Say create new"
  - "I can open the file browser"
- Instead, call upload_chat_attachments_to_folder immediately.
Example:
User: "upload files" [with attachments present]
Assistant: call choose_upload_folder with mode "files" and the attachment indexes from the most recent user message.

**UPLOAD DESTINATION RULE**
- If the user asks to upload files and the most recent user message includes attachments, do not ask the user to type a folder name in chat.
- Do not offer alternatives in plain text.
- Always call upload_chat_attachments_to_folder.
- The folder must be chosen through the folder picker UI, not by free-text chat reply.

All networks and sizes are listed below:
${JSON.stringify(state.allNetworks, null, 2)}
${(() => {
  const p = state.parsedPrompt;
  const lines = [];
  if (p) {
    if (p.image_prompt) lines.push(`- Creative brief: ${p.image_prompt}`);
    if (p.product_references?.length) {
      lines.push(`- Products featured: ${p.product_references.map(r => r.display_name || r.value).join(', ')}`);
    }
    if (p.background_scene) lines.push(`- Scene/background: ${p.background_scene}`);
    if (p.pdp_image_types?.length) lines.push(`- Image types created: ${p.pdp_image_types.join(', ')}`);
    if (p.text_elements?.length) {
      lines.push(`- Messaging/text: ${p.text_elements.map(t => t.text || t).join(', ')}`);
    }
    const pdpSize = p.pdp_ai_size || p.pdp_layered_size;
    if (pdpSize) {
      lines.push(`- Image size: ${pdpSize}`);
    } else if (state.selectedSizeIds.size > 0) {
      const sizes = state.allNetworks
        .flatMap(n => (n.placementTypes || []))
        .flatMap(pt => (pt.sizes || []))
        .filter(s => state.selectedSizeIds.has(s.id))
        .map(s => `${s.width}×${s.height}`);
      if (sizes.length) lines.push(`- Image sizes: ${[...new Set(sizes)].join(', ')}`);
    }
  }
  if (state.sessionLayoutLink) {
    const layoutUrl = state.sessionLayoutLink.startsWith('http') ? state.sessionLayoutLink : window.location.origin + state.sessionLayoutLink;
    lines.push(`- Layout link (full URL): ${layoutUrl}`);
  }
  if (state.sessionProofLink) lines.push(`- Proof link (full URL): ${state.sessionProofLink}`);
  if (!lines.length) return '';
  return `\nSESSION CONTEXT: The following content was generated in this session:\n${lines.join('\n')}\nWhen the user asks to write an email, summary, Slack message, or other communication about this work, use the above context to make the response specific and accurate. Do not use placeholders like [Brand] or [Product] when the actual values are known from the session context. If layout or proof links are present in the context, always include them as complete, full URLs exactly as provided — do not shorten, relativize, or omit them. Render each link as an HTML anchor tag so the user can click it: <a href="URL" target="_blank">URL</a>.\n`;
})()}
${state.isDescribingPlacement ? `
CONTEXT OVERRIDE: The user already has a Walmart Connect campaign with placement types and sizes selected. They are now describing the creative scene for their banners. You MUST call create_campaign with network_name="Walmart Connect" and extract background_scene and product_references from their description. Do NOT ask about or suggest placement types or sizes — they are already set.
Product/attachment rules for this context:
- Brand or product names (e.g. "Lays", "Pepsi", "Doritos") → product_references entry with type="name".
- If the user says "this background image", "using this as background", or similar AND has an attached file → add a product_references entry with type="attachment", value="0", display_name="background image", is_background=true. Do NOT also add it as a regular product.
` : ''}
${state.selectedTentpole ? `
SEASONAL/TENTPOLE CONTEXT: The user has an active seasonal/tentpole theme selected: "${state.selectedTentpole.label}". For every generate_design_concept, create_pdp_image_set, create_campaign, and create_template call made in this conversation, incorporate this theme's typical imagery, colors, props, and mood into the image_prompt/background_scene, unless the user's current message explicitly asks for a different theme.
` : ''}`
          },
          {
            role: newMessage.role,
            content: newMessage.content,
            attachments: allAttachmentsForApi.map(att => ({
              type: att.type,
              payload: att.type === 'url' ? att.payload : undefined,
            }))
          }
        ])
      );
      
      allAttachmentsForApi.forEach((att, index) => {
        if (att.type === 'file' && att.payload instanceof File) {
          formData.append(`attachments[${index}][file]`, att.payload);
        } else if (att.type === 'url') {
          formData.append(`attachments[${index}][url]`, att.payload);
        }
      });

      formData.append('model', 'gpt-5');
      formData.append('functions', JSON.stringify(creativeStudioAiFunctions));

      // Capture user message index before any handler can append messages
      const userMessageIndex = state.messages.length - 1;

      try {
        const { data: responseMessage } = await axios.post("/call-open-ai", formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (responseMessage.error) {
          throw new Error(responseMessage.error.message || 'An error occurred.');
        }

        let handlerResult = null;

        if (responseMessage.function_call) {
          const functionHandler = CHAT_FUNCTION_HANDLERS[responseMessage.function_call.name];

          if (functionHandler) {
            // Pass the guarded context so any message the handler appends after
            // its own async work is dropped if the user has since left this chat
            // (POR-13504). Handlers that build a PDP adapter from this context
            // inherit the guard through the wrapped commit/dispatch.
            handlerResult = await functionHandler(responseMessage, {
              state, commit: guard.commit, dispatch: guard.dispatch, getters,
            });
          }
        }

        // commit('UPDATE_MESSAGE', { index: userMessageIndex, payload: { attachments: responseMessage.savedAttachments ?? [] } });

        if (responseMessage.content) {
          await guard.dispatch('addAndSaveMessage', { role: "assistant", content: responseMessage.content });
        }

        // Navigate after all state is saved (used by redirect_to_pdp_images)
        if (handlerResult && handlerResult.redirectUrl) {
          window.location.href = handlerResult.redirectUrl;
          return;
        }
      } catch (e) {
        console.error('Failed to send chat message:', e);
        const apiError = e?.response?.data?.error?.message || e?.response?.data?.message;
        // Guarded: don't surface a stale generation's error into a chat the user
        // has already navigated away from. (POR-13504)
        await guard.dispatch('addAndSaveMessage', {
          role: 'assistant',
          content: apiError || 'Something went wrong. Please try again.',
        });
      } finally {
        commit('SET_PROCESSING_CHAT_MESSAGE', false);
      }
    },
    
    async fetchMessages({ dispatch, state }) {
      const savedConversationId = localStorage.getItem(activeConversationKey());

      // The user explicitly started a new (blank) chat for this company; keep it
      // blank instead of falling back to their most recent conversation.
      if (savedConversationId === NEW_CHAT_POINTER) {
        return;
      }

      if (savedConversationId) {
        try {
          await dispatch('openConversation', savedConversationId);
          return;
        } catch (e) {
          const status = e?.response?.status;

          // Only a stale or foreign pointer (404/403) should be discarded so we
          // can fall back. On transient errors (network / 5xx) keep the pointer
          // and don't switch conversations; the next load will retry.
          if (status !== 403 && status !== 404) {
            return;
          }

          localStorage.removeItem(activeConversationKey());
        }
      }

      // Fallback: open the most recent conversation for the active company.
      // `state.conversations` is already company-scoped by the backend.
      const latest = state.conversations && state.conversations[0];
      if (latest && latest.id) {
        try {
          await dispatch('openConversation', latest.id);
          localStorage.setItem(activeConversationKey(), latest.id);
        } catch (e) {
          // No valid conversation: leave the chat blank.
        }
      }
    },

    async openConversation({ commit }, conversationId) {
      commit('SET_LOADING_MESSAGES', true);

      try {
        const { data } = await axios.get(`/conversations/${conversationId}`);

        const normalizedMessages = (data.messages || []).map(normalizePersistedMessage);

        const conversationState = data.parsed_prompt || {};

        commit('SET_ACTIVE_CONVERSATION', {
          id: data.id,
          title: data.title,
          user_id: data.user_id,
          created_at: data.created_at,
          updated_at: data.updated_at,
        });

        commit('SET_MESSAGES', normalizedMessages);

        commit('SET_PARSED_PROMPT', conversationState.parsedPrompt || null);
        commit('SET_SELECTED_BACKGROUND', conversationState.selectedBackgroundUrl || null);
        commit('SET_IMAGE_PROMPT', conversationState.imagePrompt || '');
        commit('SET_SELECTED_PLACEMENT', conversationState.selectedPlacement || null);

        if (Array.isArray(conversationState.selectedSizeIds)) {
          commit('SELECT_ALL', conversationState.selectedSizeIds);
        }
      } finally {
        commit('SET_LOADING_MESSAGES', false);
      }
    },

    async createConversation({ commit }, payload = {}) {
      const { data } = await axios.post('/conversations', {
        title: payload.title || 'New chat',
      });

      commit('ADD_CONVERSATION', data);
      commit('SET_ACTIVE_CONVERSATION', data);
      commit('SET_MESSAGES', []);

      return data;
    },
    
    async saveMessages({ commit, state }, input = null) {
      if (!state.activeConversation) {
        throw new Error('No active conversation selected');
      }

      const messages = Array.isArray(input)
        ? input
        : input
          ? [input]
          : [];

      if (!messages.length) {
        return;
      }

      commit('SET_SENDING_MESSAGE', true);

      try {
        const results = [];

        for (const message of messages) {
          const formData = new FormData();

          formData.append('role', message.role || 'user');
          formData.append('content', message.content ?? '');
          formData.append('widgets', JSON.stringify(message.widgets || []));
          formData.append('_widgetValues', JSON.stringify(message._widgetValues || {}));
          formData.append('disabled', message.disabled ? '1' : '0');
          formData.append('meta', JSON.stringify(extractMessageMeta(message)));

          (message.attachments || []).forEach((attachment) => {
            if (attachment?.type === 'file' && attachment?.payload instanceof File) {
              formData.append('attachments[]', attachment.payload);
            } else if (attachment?.type === 'url' && attachment?.payload) {
              formData.append('attachments_urls[]', payloadPathname(attachment.payload));
            }
          });

          const { data } = await axios.post(
            `/conversations/${state.activeConversation.id}/messages`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          const normalized = normalizePersistedMessage(data);

          if (message.tempId) {
            commit('REPLACE_MESSAGE_BY_TEMP_ID', {
              tempId: message.tempId,
              message: normalized,
            });
          }

          results.push(normalized);
        }

        return results;
      } finally {
        commit('SET_SENDING_MESSAGE', false);
      }
    },

    async addAndSaveMessage({ commit, dispatch }, message) {
      const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const localMessage = { ...message, tempId };

      commit('ADD_MESSAGE', localMessage);

      const results = await dispatch('saveMessages', localMessage);
      return Array.isArray(results) ? results[0] : results;
    },

    // Generate the two A/B hero variants in parallel (POR-13385). Builds the
    // same Vuex-context adapter the parallel-grid flow uses and delegates to
    // startAbHeroGrid, which writes `_abHeroResult` onto the form message
    // itself (`payload.messageIndex`) in place — the unified `ab_hero` widget
    // re-renders from collecting to generating to ready on that same message,
    // no new message is appended. (POR-13765)
    async runAbHeroGrid({ state, commit, dispatch }, payload = {}) {
      const adapter = createCreativeStudioPdpContextAdapter({ state, commit, dispatch });
      await startAbHeroGrid(adapter, payload.messageIndex);
    },

    // Retry a single A/B variant tile in place (POR-13385). Same adapter as
    // runAbHeroGrid; delegates to retryAbHeroTile which patches just the one tile
    // on the existing `_abHeroResult` message.
    async retryAbHeroTile({ state, commit, dispatch }, { gridMessageIndex, rowIndex, prompt = null }) {
      const adapter = createCreativeStudioPdpContextAdapter({ state, commit, dispatch });
      await retryAbHeroTile(adapter, gridMessageIndex, rowIndex, prompt);
    },

    // Persist pendingDesignJobs + resolvedDesignJobs to localStorage. Kept
    // separate from saveMessages because the chat's saveMessages is server-
    // backed (per-message POST) and these lists are purely client-side state
    // for the cross-page recovery flow.
    persistDesignJobsState({ state }) {
      try {
        localStorage.setItem(
          PENDING_DESIGN_JOBS_KEY,
          JSON.stringify(state.pendingDesignJobs || []),
        );
      } catch (e) {
        // ignore quota — list is small
      }
      try {
        localStorage.setItem(
          RESOLVED_DESIGN_JOBS_KEY,
          JSON.stringify(state.resolvedDesignJobs || []),
        );
      } catch (e) {
        // ignore quota
      }
    },

    loadDesignJobsState({ commit }) {
      try {
        const pending = localStorage.getItem(PENDING_DESIGN_JOBS_KEY);
        if (pending) commit('SET_PENDING_DESIGN_JOBS', JSON.parse(pending));
      } catch (e) {
        // ignore corrupt entries
      }
      try {
        const resolved = localStorage.getItem(RESOLVED_DESIGN_JOBS_KEY);
        if (resolved) commit('SET_RESOLVED_DESIGN_JOBS', JSON.parse(resolved));
      } catch (e) {
        // ignore corrupt entries
      }
    },

    pushPendingDesignJob({ state, commit, dispatch }, entry) {
      // Stamp the conversation this job belongs to so a completion that arrives
      // after the user left the chat ("New" / opened another conversation) is
      // applied to the right conversation, not whatever is active on arrival.
      // Preserve an explicit conversationId if the caller already set one. (POR-13504)
      const stamped = (entry && entry.conversationId != null)
        ? entry
        : { ...entry, conversationId: state.activeConversation ? state.activeConversation.id : null };
      commit('PUSH_PENDING_DESIGN_JOB', stamped);
      dispatch('persistDesignJobsState');
    },

    resolvePendingDesignJob({ commit, dispatch }, uuid) {
      commit('REMOVE_PENDING_DESIGN_JOB', uuid);
      dispatch('persistDesignJobsState');
    },

    markDesignJobResolved({ commit, dispatch }, uuid) {
      commit('MARK_DESIGN_JOB_RESOLVED', uuid);
      dispatch('persistDesignJobsState');
    },

    // Apply the pending snapshot for a uuid using the URL from the broadcast
    // payload. Triggered by the bell's Pusher listener so the CS chat can
    // reflect the result even when the user is on a different page when the
    // event arrives — by the time they return to /creative-studio the new
    // result message is already in chat state (and persisted via the next
    // saveConversationState / message save).
    applyPendingDesignJobFromEvent({ state, commit, dispatch }, { uuid, payload }) {
      if (!uuid) return;
      // If we already added this uuid's message (e.g. duplicate broadcast), skip.
      if ((state.resolvedDesignJobs || []).includes(uuid)) return;
      const entry = (state.pendingDesignJobs || []).find((e) => e.uuid === uuid);
      if (!entry?.messageSnapshot) return;
      // Only apply the result into the currently-open chat if it belongs to the
      // conversation the job was started in. If the user has since left that chat
      // ("New" / opened another conversation), skip WITHOUT consuming the entry —
      // it stays pending so the normal cross-page recovery re-applies it into the
      // right conversation the next time that chat is opened, instead of leaking
      // into the now-active chat. Legacy entries (no conversationId) apply as
      // before. (POR-13504)
      if (entry.conversationId != null
        && entry.conversationId !== (state.activeConversation ? state.activeConversation.id : null)) {
        return;
      }

      let url = null;
      if (Array.isArray(payload?.images) && payload.images[0]?.image_url) {
        url = payload.images[0].image_url;
      } else if (Array.isArray(payload?.image_urls) && payload.image_urls[0]) {
        url = payload.image_urls[0];
      }
      if (!url) return;

      const absoluteUrl = (typeof url === 'string' && url.startsWith('http'))
        ? url
        : `${window.location.origin}${url}`;

      const message = JSON.parse(JSON.stringify(entry.messageSnapshot));
      if (Array.isArray(message.attachments)) {
        message.attachments = message.attachments.map((att) => (
          att && att.payload === '__PENDING_DESIGN_JOB_URL__'
            ? { ...att, payload: absoluteUrl }
            : att
        ));
      }
      commit('ADD_MESSAGE', message);
      commit('REMOVE_PENDING_DESIGN_JOB', uuid);
      commit('MARK_DESIGN_JOB_RESOLVED', uuid);
      dispatch('persistDesignJobsState');
      // Also persist the new message to the conversation if one is active so
      // the user sees it on next load even via a fresh page navigation.
      if (state.activeConversation?.id) {
        dispatch('saveMessages', message).catch(() => {
          // Non-fatal: the message is in memory and will be tried again next save.
        });
      }
    },
    
    async clearMessages({ commit, state, dispatch }) {
      commit('SET_IS_CLEARING_CHAT', true);

      try {
        const attachmentsToDelete = state.messages
          .flatMap(msg => msg.attachments || [])
          .filter(a => a.type === 'url' && !a.payload.startsWith('data:'))
          .map(a => a.payload);

        await axios.put('/networks/chat/clear', { attachmentsUrls: attachmentsToDelete });
      } catch (e) {
        commit('SET_IS_CLEARING_CHAT', false);
        toastr.error('Failed to clear chat messages. Please try again later.', {});
        console.error('Failed to clear chat messages:', e);
        return;
      }

      commit('SET_IS_CLEARING_CHAT', false);
      // Clear both chat history and any derived "recommended sizes"/prompt state.
      // This ensures "New" chat starts clean and avoids broken restored states.
      dispatch('resetChatSessionLocal');
    },
    async fetchConversations({ commit }) {
      commit('SET_LOADING_CONVERSATIONS', true);

      try {
        const { data } = await axios.get('/conversations');
        commit('SET_CONVERSATIONS', data);
      } finally {
        commit('SET_LOADING_CONVERSATIONS', false);
      }
    },
    async updateSavedMessage({ state }, { messageId, patch }) {
      if (!state.activeConversation?.id || !messageId) {
        return;
      }

      const formData = new FormData();

      if (Object.prototype.hasOwnProperty.call(patch, 'content')) {
        formData.append('content', patch.content ?? '');
      }

      if (Object.prototype.hasOwnProperty.call(patch, 'widgets')) {
        formData.append('widgets', JSON.stringify(patch.widgets || []));
      }

      if (Object.prototype.hasOwnProperty.call(patch, '_widgetValues')) {
        formData.append('_widgetValues', JSON.stringify(patch._widgetValues || {}));
      }

      if (Object.prototype.hasOwnProperty.call(patch, 'disabled')) {
        formData.append('disabled', patch.disabled ? '1' : '0');
      }

      const meta = extractMessageMeta(patch);

      if (Object.keys(meta).length) {
        formData.append('meta', JSON.stringify(meta));
      }

      if (Object.prototype.hasOwnProperty.call(patch, 'attachments')) {
        (patch.attachments || []).forEach((attachment) => {
          if (attachment?.type === 'file' && attachment?.payload instanceof File) {
            formData.append('attachments[]', attachment.payload);
          } else if (attachment?.type === 'url' && attachment?.payload) {
            formData.append('attachments_urls[]', payloadPathname(attachment.payload));
          }
        });
      }

      formData.append('_method', 'PATCH');

      const { data } = await axios.post(
        `/conversations/messages/${messageId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return normalizePersistedMessage(data);
    },
    async updateAndSaveMessage({ state, commit, dispatch }, { index, payload, patch }) {
      commit('UPDATE_MESSAGE', { index, payload, patch });

      const message = state.messages[index];

      if (!message?.id || String(message.id).startsWith('tmp_')) {
        return message;
      }

      // Always include the current local widgets + values in the patch.
      // Callers often mutate widget properties (e.g. selectedAction) directly
      // via this.$set(...) BEFORE calling updateAndSaveMessage with only a
      // narrow patch like { disabled: true }. Without sending the widgets
      // along, the server response would overwrite the local widget mutation
      // when REPLACE_MESSAGE_BY_ID merges in the persisted version. An
      // explicit `widgets`/`_widgetValues` in the caller's patch still wins
      // because of the ordering below.
      const mergedPatch = {
        widgets: message.widgets || [],
        _widgetValues: message._widgetValues || {},
        ...(payload ? { attachments: payload.attachments ?? [] } : {}),
        ...(patch || {}),
      };

      try {
        const saved = await dispatch('updateSavedMessage', {
          messageId: message.id,
          patch: mergedPatch,
        });

        if (saved?.id) {
          commit('REPLACE_MESSAGE_BY_ID', {
            id: saved.id,
            message: saved,
          });
        }

        return state.messages[index];
      } catch (e) {
        console.error('Failed to update message', e);
        return state.messages[index];
      }
    },
    async deleteConversation({ state, commit }, conversationId) {
      await axios.delete(`/conversations/${conversationId}`);
      commit('REMOVE_CONVERSATION', conversationId);

      if (state.activeConversation?.id === conversationId) {
        localStorage.removeItem(activeConversationKey());
      }
    },
    async addAndSaveMessages({ dispatch }, messages = []) {
      const saved = [];

      for (const message of messages) {
        saved.push(await dispatch('addAndSaveMessage', message));
      }

      return saved;
    },
    async saveConversationState({ state }) {
      if (!state.activeConversation?.id) return;

      await axios.put(`/conversations/${state.activeConversation.id}/state`, {
        parsed_prompt: {
          parsedPrompt: state.parsedPrompt || null,
          selectedBackgroundUrl: state.selectedBackgroundUrl || null,
          selectedSizeIds: Array.from(state.selectedSizeIds || []),
          imagePrompt: state.imagePrompt || '',
          selectedPlacement: state.selectedPlacement || null,
        },
      });
    },
  },
};
